import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  FileText,
  Folder,
  FolderOpen,
  ChevronRight,
  ChevronDown,
  Upload,
  Search,
  Settings,
  LogOut,
  User,
  Plus,
  Download,
  Trash2,
  Edit,
  MoreVertical,
  X,
  Loader2,
  Home,
  Eye,
  History,
  Clock,
  FileImage,
  File,
  RefreshCw,
  Filter,
  CheckSquare,
  Archive,
  FolderInput,
  FileArchive,
  Send,
  CheckCircle,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Calendar,
  PenLine,
  UserCheck,
  Users,
  Lock,
  Shield,
  Grid3X3,
  List,
  LayoutGrid,
  Table2,
  FileSearch,
  Bell,
  ChevronUp,
  BarChart3,
  Mic,
  MicOff,
  Sparkles,
  Wand2,
  Sun,
  Moon,
  Activity,
  Key,
  Building,
  Globe,
  MapPin,
  FolderTree,
  Menu,
  PanelLeftClose,
  PanelLeft
} from 'lucide-react';
import PersistentFolderSidebar, { STORAGE_KEYS, COLLAPSED_WIDTH, DEFAULT_WIDTH } from '../components/navigation/PersistentFolderSidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';

// Import refactored components
import { 
  IconsView, 
  ListView, 
  ViewTypeSwitcher, 
  PendingActionsPanel, 
  SmartSearchPanel,
  UploadModal,
  MetadataEditModal,
  WorkflowModal,
  SignatureModal,
  VersionHistoryModal,
  DocumentPreviewModal,
  AuditTrailModalInline,
  BulkMoveModal,
  NewStudyModal,
  DashboardHeader,
} from './DashboardComponents';
import { botEvents } from '../components/AssistantBot';
import MFASettings from '../components/MFASettings';
import MobileNavigation from '../components/MobileNavigation';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Workflow status colors - with proper contrast for light and dark modes
const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200', icon: Edit },
  pending_review: { label: 'Pending Review', color: 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300', icon: Clock },
  in_review: { label: 'In Review', color: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300', icon: Eye },
  approved: { label: 'Approved', color: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300', icon: XCircle },
  archived: { label: 'Archived', color: 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300', icon: Archive },
  superseded: { label: 'Superseded', color: 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300', icon: RefreshCw },
  filed: { label: 'Filed', color: 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300', icon: CheckCircle }
};

export default function DashboardPage() {
  const { user, logout, getAuthHeaders, isAdmin, isEditor, canExport, canDelete, canEditMetadata, hasLimitedEdit, canEditDocument, canManageWorkflow, canSign, canDownload, canViewAuditLogs, canCreateStudy, selectedStudy: contextStudy, clearStudySelection, getCurrentRole } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Core state
  const [studies, setStudies] = useState([]);
  const [selectedStudy, setSelectedStudy] = useState(contextStudy?.study_id || null);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [folderSubfolders, setFolderSubfolders] = useState({});
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedFolders, setExpandedFolders] = useState({});

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [smartSearchResults, setSmartSearchResults] = useState(null); // For Smart Search filtered results
  const [isListening, setIsListening] = useState(false);
  const [searchFilters, setSearchFilters] = useState({
    query: '',
    document_type: '',
    status: '',
    owner: '',
    site: '',
    country: '',
    date_from: '',
    date_to: ''
  });

  // Bulk selection state
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const [showBulkMoveModal, setShowBulkMoveModal] = useState(false);
  const [targetFolderId, setTargetFolderId] = useState('');

  // MFA Settings state
  const [mfaSettingsOpen, setMfaSettingsOpen] = useState(false);

  // Upload state
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [smartUploadModalOpen, setSmartUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadName, setUploadName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [autoClassifyEnabled, setAutoClassifyEnabled] = useState(true);
  const [autoClassifying, setAutoClassifying] = useState(false);
  const [autoClassifyConfidence, setAutoClassifyConfidence] = useState(0);
  const [suggestedDocTypes, setSuggestedDocTypes] = useState([]); // Matching document types from filename
  const [smartUploadTargetFolder, setSmartUploadTargetFolder] = useState(null);
  const [folderSuggestions, setFolderSuggestions] = useState([]);
  const [showFolderConfirmation, setShowFolderConfirmation] = useState(false);
  const [suggestingFolder, setSuggestingFolder] = useState(false);
  const [uploadMetadata, setUploadMetadata] = useState({
    document_type: '',
    site: '',  // Single site selection or "All Sites"
    sites: [], // Multiple site selection
    country: '',  // Single country selection or "All Countries"
    countries: [], // Multiple country selection
    document_date: '',
    effective_date: '',
    filing_date: '',
    receipt_date: '',
    owner: '',
    contact: '',
    artifact_classification: '',
    keywords: '',
    description: ''
  });

  // Study creation state
  const [newStudyModalOpen, setNewStudyModalOpen] = useState(false);
  const [newStudyName, setNewStudyName] = useState('');
  const [newStudyDescription, setNewStudyDescription] = useState('');
  const [creatingStudy, setCreatingStudy] = useState(false);
  // Study timeline fields
  const [studyStartDate, setStudyStartDate] = useState('');
  const [firstPatientIn, setFirstPatientIn] = useState('');
  const [lastPatientIn, setLastPatientIn] = useState('');
  const [lastPatientLastVisit, setLastPatientLastVisit] = useState('');
  const [databaseLock, setDatabaseLock] = useState('');
  const [studyCompletion, setStudyCompletion] = useState('');
  const [studyPhase, setStudyPhase] = useState('');
  const [therapeuticArea, setTherapeuticArea] = useState('');
  // Pending requirements state
  const [pendingRequirements, setPendingRequirements] = useState([]);
  const [documentSchedule, setDocumentSchedule] = useState(null);

  // Preview and versioning state
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [documentVersions, setDocumentVersions] = useState([]);
  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [selectedDocForVersion, setSelectedDocForVersion] = useState(null);
  const [newVersionFile, setNewVersionFile] = useState(null);
  const [uploadingVersion, setUploadingVersion] = useState(false);

  // Audit and workflow state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditModalOpen, setAuditModalOpen] = useState(false);
  const [workflowModalOpen, setWorkflowModalOpen] = useState(false);
  const [selectedDocForWorkflow, setSelectedDocForWorkflow] = useState(null);
  const [workflowComment, setWorkflowComment] = useState('');
  const [workflowAssignees, setWorkflowAssignees] = useState([{ email: '', role: 'reviewer' }]);
  const [processingWorkflow, setProcessingWorkflow] = useState(false);
  
  // Signature states
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signatureText, setSignatureText] = useState('');
  const [signatureMeaning, setSignatureMeaning] = useState('');
  const [signaturePassword, setSignaturePassword] = useState(''); // For 21 CFR Part 11 re-authentication
  const [signatureRole, setSignatureRole] = useState(''); // Role for signature
  const [processingSignature, setProcessingSignature] = useState(false);

  // Metadata edit state
  const [metadataModalOpen, setMetadataModalOpen] = useState(false);
  const [selectedDocForMetadata, setSelectedDocForMetadata] = useState(null);
  const [editingMetadata, setEditingMetadata] = useState({});
  const [savingMetadata, setSavingMetadata] = useState(false);

  // Document types
  const [documentTypes, setDocumentTypes] = useState([]);
  const [allDocumentTypes, setAllDocumentTypes] = useState([]);
  
  // Track folder hierarchy for artifact classification
  const [currentFolderPath, setCurrentFolderPath] = useState({ mainFolder: '', subFolder: '' });

  // Export state
  const [exporting, setExporting] = useState(false);

  // View type state
  const [viewType, setViewType] = useState('tiles'); // icons, list, details, tiles, content

  // All study documents state and sorting
  const [allStudyDocuments, setAllStudyDocuments] = useState([]);
  const [sortBy, setSortBy] = useState('latest'); // latest, oldest, folder, status
  const [showAllDocs, setShowAllDocs] = useState(true); // Toggle between all docs and folder-specific

  // Pending actions state
  const [pendingActions, setPendingActions] = useState([]);
  const [showPendingActions, setShowPendingActions] = useState(true);
  const [loadingPendingActions, setLoadingPendingActions] = useState(false);

  // Mobile sidebar state
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Sidebar collapse state (synced with PersistentFolderSidebar via localStorage)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.COLLAPSED);
    return saved === 'true';
  });
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.WIDTH);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });

  // Track if we're on desktop to apply sidebar margin
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1024);

  // Listen for sidebar state changes from localStorage and window resize
  useEffect(() => {
    const handleStorageChange = () => {
      const collapsed = localStorage.getItem(STORAGE_KEYS.COLLAPSED) === 'true';
      const width = parseInt(localStorage.getItem(STORAGE_KEYS.WIDTH) || DEFAULT_WIDTH, 10);
      setSidebarCollapsed(collapsed);
      setSidebarWidth(width);
    };

    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    // Listen for storage changes from other tabs/components
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('resize', handleResize);
    
    // Also poll for changes within the same tab (localStorage doesn't fire events for same-tab changes)
    const interval = setInterval(handleStorageChange, 100);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('resize', handleResize);
      clearInterval(interval);
    };
  }, []);

  // Study sites and countries for Smart Upload
  const [studySites, setStudySites] = useState([]);
  const [studyCountries, setStudyCountries] = useState([]);

  useEffect(() => {
    fetchStudies();
    fetchDocumentTypes();
    fetchAllDocumentTypes();
  }, []);

  // Handle URL parameters for navigation and bot commands
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('view') === 'all') {
      setShowAllDocs(true);
      setSelectedFolder(null);
      setSmartSearchResults(null);
    }
    
    // Handle bot commands via URL params
    const viewTypeParam = params.get('viewType');
    if (viewTypeParam && ['tiles', 'list', 'details', 'icons', 'content'].includes(viewTypeParam)) {
      setViewType(viewTypeParam);
    }
    
    const searchParam = params.get('search');
    if (searchParam) {
      setSearchQuery(searchParam);
    }
    
    const smartSearchParam = params.get('smartSearch');
    if (smartSearchParam === 'true') {
      setShowSearchModal(true);
    }
    
    const uploadParam = params.get('upload');
    if (uploadParam === 'true') {
      setSmartUploadModalOpen(true);
    }
  }, [location.search]);

  // Listen for bot events
  useEffect(() => {
    const unsubscribeView = botEvents.on('viewChange', (newView) => {
      setViewType(newView);
    });
    
    const unsubscribeSearch = botEvents.on('search', (term) => {
      setSearchQuery(term);
    });
    
    const unsubscribeSmartSearch = botEvents.on('smartSearch', () => {
      setShowSearchModal(true);
    });
    
    const unsubscribeUpload = botEvents.on('upload', () => {
      setSmartUploadModalOpen(true);
    });
    
    return () => {
      unsubscribeView();
      unsubscribeSearch();
      unsubscribeSmartSearch();
      unsubscribeUpload();
    };
  }, []);

  useEffect(() => {
    if (selectedStudy) {
      fetchFolders(selectedStudy);
      fetchAllStudyDocuments(selectedStudy); // Fetch all documents for the study
      fetchPendingRequirements(selectedStudy); // Fetch pending document requirements
      fetchDocumentSchedule(selectedStudy); // Fetch document schedule
      setSelectedFolder(null);
      setFolderSubfolders({});
      setDocuments([]);
      setExpandedFolders({});
      setSelectedDocs(new Set());
      setShowAllDocs(true); // Show all docs by default
    }
  }, [selectedStudy]);

  useEffect(() => {
    if (selectedFolder) {
      fetchDocuments(selectedFolder.id);
      setSelectedDocs(new Set());
      setShowAllDocs(false); // Switch to folder-specific view
    }
  }, [selectedFolder]);

  const fetchStudies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/studies`, getAuthHeaders());
      setStudies(response.data);
      if (response.data.length > 0 && !selectedStudy) {
        setSelectedStudy(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to fetch studies');
    } finally {
      setLoading(false);
    }
  };

  const fetchFolders = async (studyId) => {
    try {
      const response = await axios.get(`${API_URL}/api/studies/${studyId}/folders`, getAuthHeaders());
      setFolders(response.data);
    } catch (error) {
      toast.error('Failed to fetch folders');
    }
  };

  // Fetch all documents for the entire study
  const fetchAllStudyDocuments = async (studyId) => {
    try {
      const response = await axios.get(`${API_URL}/api/studies/${studyId}/all-documents`, getAuthHeaders());
      setAllStudyDocuments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch all study documents:', error);
      setAllStudyDocuments([]);
    }
  };

  const fetchSubfolders = async (folderId) => {
    try {
      const response = await axios.get(`${API_URL}/api/folders/${folderId}/subfolders`, getAuthHeaders());
      setFolderSubfolders(prev => ({
        ...prev,
        [folderId]: response.data
      }));
    } catch (error) {
      console.error('Failed to fetch subfolders:', error);
    }
  };

  const fetchDocuments = async (folderId) => {
    try {
      const response = await axios.get(`${API_URL}/api/folders/${folderId}/documents`, getAuthHeaders());
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to fetch documents');
    }
  };

  // Fetch pending actions for the current user
  const fetchPendingActions = async () => {
    if (!user?.email) return;
    
    setLoadingPendingActions(true);
    try {
      // Use the dedicated pending-actions endpoint
      const response = await axios.get(`${API_URL}/api/documents/pending-actions`, getAuthHeaders());
      setPendingActions(response.data || []);
    } catch (error) {
      console.error('Failed to fetch pending actions:', error);
      setPendingActions([]);
    } finally {
      setLoadingPendingActions(false);
    }
  };

  // Fetch pending actions on component mount and when user changes
  useEffect(() => {
    if (user?.email) {
      fetchPendingActions();
    }
  }, [user?.email]);

  const fetchDocumentTypes = async (folderId = null) => {
    try {
      let response;
      if (folderId) {
        // Fetch folder-specific document types
        response = await axios.get(`${API_URL}/api/document-types/folder/${folderId}`, getAuthHeaders());
      } else {
        // Fetch general document types
        response = await axios.get(`${API_URL}/api/document-types`, getAuthHeaders());
      }
      setDocumentTypes(response.data.document_types || []);
    } catch (error) {
      console.error('Failed to fetch document types');
      // Fallback to general types
      try {
        const response = await axios.get(`${API_URL}/api/document-types`, getAuthHeaders());
        setDocumentTypes(response.data.document_types || []);
      } catch (e) {
        console.error('Failed to fetch fallback document types');
      }
    }
  };

  // Fetch ALL document types (general + all subfolder-specific) for Smart Upload
  const fetchAllDocumentTypes = async () => {
    try {
      // Fetch general types
      const generalResponse = await axios.get(`${API_URL}/api/document-types`, getAuthHeaders());
      const generalTypes = generalResponse.data.document_types || [];
      
      // Fetch subfolder-specific types
      const subfolderResponse = await axios.get(`${API_URL}/api/subfolder-document-types`, getAuthHeaders());
      const subfolderTypes = subfolderResponse.data.subfolder_document_types || {};
      
      // Combine all types into a unique set
      const allTypes = new Set(generalTypes);
      Object.values(subfolderTypes).forEach(types => {
        types.forEach(type => allTypes.add(type));
      });
      
      setAllDocumentTypes([...allTypes].sort());
    } catch (error) {
      console.error('Failed to fetch all document types:', error);
      setAllDocumentTypes(documentTypes); // Fallback to current types
    }
  };

  // Fetch study sites and countries for dropdowns
  const fetchStudySitesAndCountries = async (studyId) => {
    if (!studyId) return;
    try {
      const response = await axios.get(`${API_URL}/api/studies/${studyId}/sites`, getAuthHeaders());
      setStudySites(response.data.sites || []);
      setStudyCountries(response.data.countries || []);
    } catch (error) {
      console.error('Failed to fetch study sites and countries:', error);
      setStudySites([]);
      setStudyCountries([]);
    }
  };

  // Fetch sites when study changes
  useEffect(() => {
    if (selectedStudy) {
      fetchStudySitesAndCountries(selectedStudy);
    }
  }, [selectedStudy]);

  const handleFolderClick = async (folder) => {
    setSelectedFolder(folder);
    const isExpanding = !expandedFolders[folder.id];
    setExpandedFolders(prev => ({
      ...prev,
      [folder.id]: isExpanding
    }));
    
    // Track folder path for artifact classification - this is a main folder
    setCurrentFolderPath({ mainFolder: folder.name, subFolder: '' });
    
    // Fetch folder-specific document types
    fetchDocumentTypes(folder.id);
    
    if (isExpanding && !folderSubfolders[folder.id]) {
      await fetchSubfolders(folder.id);
    }
  };

  const handleSubfolderClick = (subfolder, parentFolder) => {
    setSelectedFolder(subfolder);
    fetchDocuments(subfolder.id);
    fetchDocumentTypes(subfolder.id); // Fetch folder-specific document types
    
    // Track folder path for artifact classification
    setCurrentFolderPath({ mainFolder: parentFolder?.name || '', subFolder: subfolder.name });
  };

  // Auto-generate artifact classification
  const generateArtifactClassification = (docType, targetFolder = null) => {
    const parts = [];
    
    // For Smart Upload, use the target folder info
    if (targetFolder) {
      if (targetFolder.parentName) parts.push(targetFolder.parentName);
      if (targetFolder.name) parts.push(targetFolder.name);
    } else {
      // For regular upload, use currentFolderPath
      if (currentFolderPath.mainFolder) parts.push(currentFolderPath.mainFolder);
      if (currentFolderPath.subFolder) parts.push(currentFolderPath.subFolder);
    }
    
    if (docType) parts.push(docType);
    return parts.join(' > ');
  };

  // Get sorted document types with suggested ones first
  const getSortedDocumentTypes = () => {
    const allTypes = allDocumentTypes.length > 0 ? allDocumentTypes : documentTypes;
    if (!allTypes.length) return [];
    
    // If no suggestions, return alphabetically sorted
    if (!suggestedDocTypes.length) return [...allTypes].sort();
    
    // Separate suggested and non-suggested types
    const suggested = [];
    const others = [];
    
    allTypes.forEach(type => {
      if (suggestedDocTypes.includes(type)) {
        suggested.push(type);
      } else {
        others.push(type);
      }
    });
    
    // Sort each group and combine (suggested first)
    return [
      ...suggested.sort((a, b) => suggestedDocTypes.indexOf(a) - suggestedDocTypes.indexOf(b)),
      ...others.sort()
    ];
  };

  // Handle document type selection and auto-populate artifact classification
  const handleDocumentTypeChange = (docType) => {
    // Use smartUploadTargetFolder if in Smart Upload mode, otherwise use currentFolderPath
    const artifactClass = generateArtifactClassification(docType, smartUploadTargetFolder);
    setUploadMetadata(prev => ({
      ...prev,
      document_type: docType,
      artifact_classification: artifactClass
    }));
    
    // If in Smart Upload and document type changed, re-suggest folder
    if (smartUploadModalOpen && docType && selectedStudy) {
      suggestFolderForDocType(docType);
    }
  };

  // Get all uploadable subfolders for smart upload
  const getUploadableFolders = () => {
    const uploadable = [];
    folders.forEach(folder => {
      if (folder.subfolders) {
        folder.subfolders.forEach(sub => {
          uploadable.push({
            ...sub,
            parentName: folder.name,
            fullPath: `${folder.name} → ${sub.name}`
          });
        });
      }
    });
    return uploadable;
  };

  // Check if the selected folder is a main/parent folder (not a subfolder)
  const isMainFolder = (folder) => {
    if (!folder) return false;
    // A main folder has no parent_id or its parent_id doesn't exist in our folders
    return !folder.parent_id;
  };

  // Handle Smart Upload (with folder selection)
  const handleSmartUpload = async () => {
    if (!uploadFile || !uploadName || !smartUploadTargetFolder) {
      toast.error('Please select a file and target folder');
      return;
    }

    if (!uploadMetadata.document_type || !uploadMetadata.document_date) {
      toast.error('Document Type and Document Date are required');
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        const fileType = uploadFile.name.split('.').pop().toLowerCase();

        await axios.post(`${API_URL}/api/documents`, {
          folder_id: smartUploadTargetFolder.id,
          study_id: selectedStudy,
          name: uploadName,
          file_content: base64,
          file_type: fileType,
          metadata: {
            ...uploadMetadata,
            keywords: uploadMetadata.keywords ? uploadMetadata.keywords.split(',').map(k => k.trim()) : []
          }
        }, getAuthHeaders());

        toast.success('Document uploaded and filed successfully');
        setSmartUploadModalOpen(false);
        resetUploadForm();
        
        // Refresh folders to ensure folder data is up to date
        if (selectedStudy) {
          await fetchFolders(selectedStudy);
        }
        
        // Refresh documents if we're viewing all docs
        if (showAllDocs && selectedStudy) {
          fetchAllStudyDocuments(selectedStudy);
        }
      };
      reader.readAsDataURL(uploadFile);
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadName || !selectedFolder) {
      toast.error('Please fill required fields');
      return;
    }

    if (!uploadMetadata.document_type || !uploadMetadata.document_date) {
      toast.error('Document Type and Document Date are required');
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        const fileType = uploadFile.name.split('.').pop().toLowerCase();

        await axios.post(`${API_URL}/api/documents`, {
          folder_id: selectedFolder.id,
          study_id: selectedStudy,
          name: uploadName,
          file_content: base64,
          file_type: fileType,
          metadata: {
            ...uploadMetadata,
            keywords: uploadMetadata.keywords ? uploadMetadata.keywords.split(',').map(k => k.trim()) : []
          }
        }, getAuthHeaders());

        toast.success('Document uploaded and filed successfully');
        setUploadModalOpen(false);
        resetUploadForm();
        fetchDocuments(selectedFolder.id);
      };
      reader.readAsDataURL(uploadFile);
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadName('');
    setAutoClassifyConfidence(0);
    setSuggestedDocTypes([]);  // Reset suggested document types
    setSmartUploadTargetFolder(null);
    setFolderSuggestions([]);
    setShowFolderConfirmation(false);
    setUploadMetadata({
      document_type: '',
      site: '',
      country: '',
      document_date: '',
      effective_date: '',
      filing_date: '',
      receipt_date: '',
      owner: '',
      contact: '',
      artifact_classification: '',
      keywords: '',
      description: ''
    });
  };

  // Auto-classify document based on filename
  const handleAutoClassify = async (filename, targetFolderId = null) => {
    if (!autoClassifyEnabled || !filename) return;
    
    setAutoClassifying(true);
    try {
      const folderId = targetFolderId || smartUploadTargetFolder?.id || selectedFolder?.id || null;
      const response = await axios.post(`${API_URL}/api/documents/auto-classify`, {
        filename: filename,
        folder_id: folderId
      }, getAuthHeaders());
      
      const { document_type, artifact_classification, site, country, keywords, confidence, suggested_types } = response.data;
      
      setAutoClassifyConfidence(confidence);
      
      // Update suggested document types for dropdown prioritization
      if (suggested_types && suggested_types.length > 0) {
        setSuggestedDocTypes(suggested_types);
      } else if (document_type) {
        setSuggestedDocTypes([document_type]);
      }
      
      // Update metadata with auto-classified values (only if not already set)
      setUploadMetadata(prev => ({
        ...prev,
        document_type: document_type || prev.document_type,
        artifact_classification: artifact_classification || prev.artifact_classification,
        site: site || prev.site,
        country: country || prev.country,
        keywords: keywords?.length > 0 ? keywords.join(', ') : prev.keywords
      }));
      
      if (confidence > 0.5) {
        toast.success(`Auto-classified with ${Math.round(confidence * 100)}% confidence`);
      } else if (confidence > 0) {
        toast.info(`Suggested classification (${Math.round(confidence * 100)}% confidence) - please verify`);
      }
      
      // If we detected a document type, suggest a folder
      if (document_type && selectedStudy) {
        await suggestFolderForDocType(document_type);
      }
    } catch (error) {
      console.error('Auto-classify failed:', error);
      // Silently fail - user can still manually classify
    } finally {
      setAutoClassifying(false);
    }
  };

  // Suggest folder based on document type
  const suggestFolderForDocType = async (documentType) => {
    if (!documentType || !selectedStudy) return;
    
    setSuggestingFolder(true);
    try {
      const response = await axios.post(`${API_URL}/api/documents/suggest-folder`, {
        document_type: documentType,
        study_id: selectedStudy
      }, getAuthHeaders());
      
      const { suggestions, exact_match, multiple_matches } = response.data;
      
      if (suggestions.length > 0) {
        setFolderSuggestions(suggestions);
        
        const selectFolder = (bestMatch) => {
          const folderObj = {
            id: bestMatch.folder_id,
            name: bestMatch.folder_name,
            parentName: bestMatch.parent_name,
            fullPath: bestMatch.full_path
          };
          setSmartUploadTargetFolder(folderObj);
          
          // Use artifact classification from backend if available, otherwise generate it
          const artifactClass = bestMatch.artifact_classification || generateArtifactClassification(documentType, folderObj);
          setUploadMetadata(prev => ({
            ...prev,
            artifact_classification: artifactClass
          }));
          
          return folderObj;
        };
        
        if (exact_match) {
          // Single exact match - auto-select it but still show other options
          const bestMatch = suggestions[0];
          selectFolder(bestMatch);
          // Show folder suggestions so user can see alternatives
          if (suggestions.length > 1) {
            setShowFolderConfirmation(true);
          }
          toast.success(`Auto-selected folder: ${bestMatch.full_path}`);
        } else if (multiple_matches) {
          // Multiple matches - show confirmation
          setShowFolderConfirmation(true);
          toast.info('Multiple matching folders found. Please select one.');
        } else if (suggestions.length === 1) {
          // Single suggestion (not exact match)
          const bestMatch = suggestions[0];
          selectFolder(bestMatch);
          toast.info(`Suggested folder: ${bestMatch.full_path}`);
        } else {
          // Multiple partial matches - show all options
          setShowFolderConfirmation(true);
        }
      }
    } catch (error) {
      console.error('Folder suggestion failed:', error);
    } finally {
      setSuggestingFolder(false);
    }
  };

  // Handle file selection with auto-classify
  const handleFileSelect = (file) => {
    if (!file) return;
    
    setUploadFile(file);
    
    // Auto-set document name from filename (without extension)
    const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
    setUploadName(nameWithoutExt);
    
    // Auto-fill dates
    const today = new Date().toISOString().split('T')[0];
    
    // Check if user can approve documents (no approval workflow required)
    const canApprove = user?.permissions?.can_approve || 
                       ['system_admin', 'admin', 'tmf_lead', 'regulatory_affairs', 'qc_reviewer'].includes(user?.role);
    
    setUploadMetadata(prev => ({
      ...prev,
      document_date: prev.document_date || today,
      filing_date: today, // Always set to upload date
      // If user can approve, set effective and receipt dates to today
      effective_date: canApprove ? (prev.effective_date || today) : prev.effective_date,
      receipt_date: canApprove ? (prev.receipt_date || today) : prev.receipt_date
    }));
    
    // Trigger auto-classification
    if (autoClassifyEnabled) {
      handleAutoClassify(file.name);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API_URL}/api/documents/${doc.id}`, getAuthHeaders());
      const { file_content, file_type, name } = response.data;
      
      const byteCharacters = atob(file_content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray]);
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${name}.${file_type}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handlePreview = async (doc) => {
    setPreviewDocument(doc);
    setPreviewLoading(true);
    setPreviewModalOpen(true);
    
    try {
      // Use the preview endpoint for better handling of different file types
      const response = await axios.get(`${API_URL}/api/documents/${doc.id}/preview`, getAuthHeaders());
      setPreviewContent(response.data);
    } catch (error) {
      // Fallback to regular document fetch
      try {
        const fallbackResponse = await axios.get(`${API_URL}/api/documents/${doc.id}`, getAuthHeaders());
        setPreviewContent(fallbackResponse.data);
      } catch (fallbackError) {
        toast.error('Failed to load document preview');
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleViewVersions = async (doc) => {
    setSelectedDocForVersion(doc);
    setVersionModalOpen(true);
    
    try {
      const response = await axios.get(`${API_URL}/api/documents/${doc.id}/versions`, getAuthHeaders());
      setDocumentVersions(response.data);
    } catch (error) {
      setDocumentVersions([]);
    }
  };

  // Handle regulatory-compliant version upload (21 CFR Part 11)
  const handleUploadNewVersion = async (versionData) => {
    if (!versionData?.file || !selectedDocForVersion) return;

    setUploadingVersion(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        const fileType = versionData.file.name.split('.').pop().toLowerCase();

        // Send regulatory-compliant version upload request
        const response = await axios.post(`${API_URL}/api/documents/${selectedDocForVersion.id}/versions`, {
          file_content: base64,
          file_type: fileType,
          // Mandatory metadata
          update_description: versionData.updateDescription,
          change_summary: versionData.changeSummary,
          impacted_sections: versionData.impactedSections || null,
          // Mandatory workflow status
          workflow_status: versionData.workflowStatus,
          assigned_reviewers: versionData.assignedReviewers ? versionData.assignedReviewers.split(',').map(e => e.trim()).filter(e => e) : null,
          // Mandatory reason
          reason_for_update: versionData.reasonForUpdate,
          reason_category: versionData.reasonCategory,
          // Mandatory embedded comparison (NOT separate document)
          comparison_summary: versionData.comparisonSummary,
          comparison_details: versionData.comparisonDetails || null,
          // Optional
          effective_date: versionData.effectiveDate || null,
          regulatory_reference: versionData.regulatoryReference || null,
        }, getAuthHeaders());

        toast.success('Regulatory-compliant version uploaded successfully!');
        setVersionModalOpen(false);
        
        // Refresh documents
        if (selectedFolder) {
          await fetchDocuments(selectedFolder.id);
        }
        
        // Auto-trigger workflow assignment for the newly uploaded version
        if (response.data) {
          const updatedDoc = response.data;
          setSelectedDocForWorkflow(updatedDoc);
          setWorkflowAssignees([{ email: '', role: 'reviewer', signature_required: false }]);
          setWorkflowComment('');
          setWorkflowModalOpen(true);
          toast.info('Please assign reviewers for the new version.', { duration: 5000 });
        }
      };
      reader.readAsDataURL(versionData.file);
    } catch (error) {
      // Handle validation errors from backend
      if (error.response?.status === 422) {
        const detail = error.response.data?.detail;
        if (detail?.validation_errors) {
          toast.error(
            <div>
              <p className="font-bold">Compliance Validation Failed</p>
              <ul className="text-xs mt-1 list-disc list-inside">
                {detail.validation_errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </div>,
            { duration: 10000 }
          );
        } else {
          toast.error(detail?.message || 'Version upload rejected - compliance requirements not met');
        }
      } else {
        toast.error('Failed to upload new version');
      }
    } finally {
      setUploadingVersion(false);
    }
  };

  const handleDownloadVersion = async (version) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/documents/${selectedDocForVersion.id}/versions/${version.version}`,
        getAuthHeaders()
      );
      const { file_content, file_type } = response.data;
      
      const byteCharacters = atob(file_content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray]);
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedDocForVersion.name}_v${version.version}.${file_type}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download version');
    }
  };

  const handleViewAuditLog = async (doc) => {
    setSelectedDocForVersion(doc);
    setAuditModalOpen(true);
    
    try {
      const response = await axios.get(`${API_URL}/api/admin/audit-logs/document/${doc.id}`, getAuthHeaders());
      setAuditLogs(response.data);
    } catch (error) {
      setAuditLogs([]);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await axios.delete(`${API_URL}/api/documents/${docId}`, getAuthHeaders());
      toast.success('Document deleted');
      fetchDocuments(selectedFolder.id);
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  // Workflow actions
  const handleWorkflowAction = async (action) => {
    if (!selectedDocForWorkflow) return;

    // Filter out empty assignees and format with roles
    const validAssignees = workflowAssignees
      .filter(a => a.email && a.email.trim().length > 0)
      .map(a => ({ email: a.email.trim(), role: a.role, name: null }));

    setProcessingWorkflow(true);
    try {
      await axios.post(`${API_URL}/api/documents/${selectedDocForWorkflow.id}/workflow`, {
        action,
        comment: workflowComment,
        assignees: validAssignees.length > 0 ? validAssignees : null
      }, getAuthHeaders());

      toast.success(`Document ${action.replace(/_/g, ' ')} successfully`);
      setWorkflowModalOpen(false);
      setWorkflowComment('');
      setWorkflowAssignees([{ email: '', role: 'reviewer' }]);
      fetchDocuments(selectedFolder.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Workflow action failed');
    } finally {
      setProcessingWorkflow(false);
    }
  };

  // Add/remove assignee
  const addAssignee = () => {
    setWorkflowAssignees([...workflowAssignees, { email: '', role: 'reviewer', signature_required: false }]);
  };

  const removeAssignee = (index) => {
    setWorkflowAssignees(workflowAssignees.filter((_, i) => i !== index));
  };

  const updateAssignee = (index, field, value) => {
    const updated = [...workflowAssignees];
    updated[index][field] = value;
    setWorkflowAssignees(updated);
  };

  // Electronic signature - 21 CFR Part 11 compliant
  const handleSignDocument = async () => {
    if (!selectedDocForWorkflow || !signatureText || !signaturePassword) {
      toast.error('Please complete all required fields including password');
      return;
    }

    // Determine signer's role from assignment or selected role
    const assignees = selectedDocForWorkflow.assignees_with_roles || [];
    const myAssignment = assignees.find(a => a.email?.toLowerCase() === user?.email?.toLowerCase());
    const role = signatureRole || myAssignment?.role || 'reviewer';

    setProcessingSignature(true);
    try {
      // First verify password (re-authentication per 21 CFR Part 11)
      const authResponse = await axios.post(`${API_URL}/api/auth/verify-password`, {
        email: user.email,
        password: signaturePassword
      }, getAuthHeaders());

      if (!authResponse.data.verified) {
        toast.error('Password verification failed. Please enter your correct password.');
        setProcessingSignature(false);
        return;
      }

      // Now submit the signature
      await axios.post(`${API_URL}/api/documents/${selectedDocForWorkflow.id}/sign`, {
        signer_email: user.email,
        signer_name: user.name,
        role: role,
        signature_text: signatureText,
        signature_meaning: signatureMeaning || `I have ${role === 'approver' ? 'approved' : role === 'qc' ? 'completed QC review of' : 'reviewed'} this document`
      }, getAuthHeaders());

      toast.success('Document signed successfully - 21 CFR Part 11 compliant');
      setSignatureModalOpen(false);
      setSignatureText('');
      setSignatureMeaning('');
      setSignaturePassword('');
      setSignatureRole('');
      if (selectedFolder) {
        fetchDocuments(selectedFolder.id);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Password verification failed. Please try again.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to sign document');
      }
    } finally {
      setProcessingSignature(false);
    }
  };

  // Check if folder allows uploads (only subfolders, not main folders)
  const canUploadToFolder = (folder) => {
    if (!folder) return false;
    // Main folders have parent_id as null, subfolders have a parent_id
    return folder.parent_id !== null && folder.parent_id !== undefined;
  };

  // Check if current user can sign the document
  const canSignDocument = (doc) => {
    if (!doc || !user) return false;
    
    // Document must be in a signable status
    const signableStatuses = ['pending_review', 'in_review', 'draft'];
    if (!signableStatuses.includes(doc.status)) return false;
    
    // Check if user has already signed this document
    const signatures = doc.signatures || [];
    const hasSigned = signatures.some(s => s.signer_email?.toLowerCase() === user.email?.toLowerCase());
    if (hasSigned) return false;
    
    // Check if user is assigned AND signature is required for them
    const assignees = doc.assignees_with_roles || [];
    const userAssignment = assignees.find(a => a.email?.toLowerCase() === user.email?.toLowerCase());
    
    // Only allow signing if:
    // 1. User is assigned to this document
    // 2. Signature is required for their assignment (signature_required === true)
    if (userAssignment && userAssignment.signature_required === true) {
      return true;
    }
    
    return false;
  };

  // Check if document needs signatures (only from those with signature_required = true)
  const documentNeedsSignature = (doc) => {
    if (!doc) return false;
    const assignees = doc.assignees_with_roles || [];
    const signatures = doc.signatures || [];
    
    // Count only assignees who have signature_required = true
    const assigneesRequiringSignature = assignees.filter(a => a.signature_required === true);
    if (assigneesRequiringSignature.length === 0) return false;
    
    // Check how many of those have actually signed
    const signedEmails = signatures.map(s => s.signer_email?.toLowerCase());
    const pendingSignatures = assigneesRequiringSignature.filter(
      a => !signedEmails.includes(a.email?.toLowerCase())
    );
    
    return pendingSignatures.length > 0;
  };

  // Get signature status counts
  const getSignatureStatus = (doc) => {
    if (!doc) return { required: 0, signed: 0, pending: 0 };
    
    const assignees = doc.assignees_with_roles || [];
    const signatures = doc.signatures || [];
    
    // Only count assignees with signature_required = true
    const assigneesRequiringSignature = assignees.filter(a => a.signature_required === true);
    const signedEmails = signatures.map(s => s.signer_email?.toLowerCase());
    
    const signed = assigneesRequiringSignature.filter(
      a => signedEmails.includes(a.email?.toLowerCase())
    ).length;
    
    return {
      required: assigneesRequiringSignature.length,
      signed: signed,
      pending: assigneesRequiringSignature.length - signed
    };
  };

  // Get display documents based on view mode and sorting
  const getDisplayDocuments = () => {
    // If smart search results are active, use them
    if (smartSearchResults !== null) {
      return smartSearchResults;
    }
    
    let docs = showAllDocs ? allStudyDocuments : documents;
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      docs = docs.filter(doc => 
        doc.name?.toLowerCase().includes(query) ||
        doc.document_type?.toLowerCase().includes(query) ||
        doc.owner?.toLowerCase().includes(query)
      );
    }
    
    // Apply site filter
    if (searchFilters.site) {
      docs = docs.filter(doc => {
        const docSite = doc.site || doc.metadata?.site;
        const docSites = doc.sites || doc.metadata?.sites || [];
        return docSite === searchFilters.site || 
               docSite === 'All Sites' ||
               docSites.includes(searchFilters.site);
      });
    }
    
    // Apply country filter
    if (searchFilters.country) {
      docs = docs.filter(doc => {
        const docCountry = doc.country || doc.metadata?.country;
        const docCountries = doc.countries || doc.metadata?.countries || [];
        return docCountry === searchFilters.country || 
               docCountry === 'All Countries' ||
               docCountries.includes(searchFilters.country);
      });
    }
    
    // Apply sorting
    switch (sortBy) {
      case 'latest':
        return [...docs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      case 'oldest':
        return [...docs].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      case 'folder':
        return [...docs].sort((a, b) => (a.folder_id || '').localeCompare(b.folder_id || ''));
      case 'status':
        const statusOrder = ['approved', 'in_review', 'pending_review', 'draft', 'rejected', 'archived', 'superseded'];
        return [...docs].sort((a, b) => statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status));
      case 'name':
        return [...docs].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      case 'compliance':
        return [...docs].sort((a, b) => (a.compliance_score || 100) - (b.compliance_score || 100));
      default:
        return docs;
    }
  };

  // Clear smart search results
  const clearSmartSearch = () => {
    setSmartSearchResults(null);
  };

  // Get folder name by ID for display
  const getFolderName = (folderId) => {
    if (!folderId) return 'Unknown Folder';
    
    // Search in main folders
    const mainFolder = folders.find(f => f.id === folderId);
    if (mainFolder) return mainFolder.name;
    
    // Search in subfolders (from folderSubfolders state)
    for (const subfolders of Object.values(folderSubfolders)) {
      const subfolder = subfolders.find(sf => sf.id === folderId);
      if (subfolder) return subfolder.name;
    }
    
    // Also search in folders array's nested subfolders
    for (const folder of folders) {
      if (folder.subfolders) {
        const subfolder = folder.subfolders.find(sf => sf.id === folderId);
        if (subfolder) return subfolder.name;
      }
    }
    
    return 'Unknown Folder';
  };

  // Get full folder path by ID for display
  const getFolderPath = (folderId) => {
    if (!folderId) return 'Unknown Folder';
    
    // Search in main folders
    const mainFolder = folders.find(f => f.id === folderId);
    if (mainFolder) return mainFolder.name;
    
    // Search in folders array's nested subfolders
    for (const folder of folders) {
      if (folder.subfolders) {
        const subfolder = folder.subfolders.find(sf => sf.id === folderId);
        if (subfolder) return `${folder.name} → ${subfolder.name}`;
      }
    }
    
    // Search in folderSubfolders state
    for (const [parentId, subfolders] of Object.entries(folderSubfolders)) {
      const subfolder = subfolders.find(sf => sf.id === folderId);
      if (subfolder) {
        const parent = folders.find(f => f.id === parentId);
        return parent ? `${parent.name} → ${subfolder.name}` : subfolder.name;
      }
    }
    
    return 'Unknown Folder';
  };

  // Metadata editing
  const handleEditMetadata = (doc) => {
    setSelectedDocForMetadata(doc);
    setEditingMetadata({
      document_type: doc.document_type || '',
      site: doc.site || '',
      country: doc.country || '',
      document_date: doc.document_date || '',
      effective_date: doc.effective_date || '',
      filing_date: doc.filing_date || '',
      receipt_date: doc.receipt_date || '',
      owner: doc.owner || '',
      contact: doc.contact || '',
      artifact_classification: doc.artifact_classification || '',
      keywords: (doc.keywords || []).join(', '),
      description: doc.description || ''
    });
    setMetadataModalOpen(true);
  };

  const handleSaveMetadata = async () => {
    if (!editingMetadata.document_type || !editingMetadata.document_date) {
      toast.error('Document Type and Document Date are required');
      return;
    }

    setSavingMetadata(true);
    try {
      await axios.put(`${API_URL}/api/documents/${selectedDocForMetadata.id}`, {
        metadata: {
          ...editingMetadata,
          keywords: editingMetadata.keywords ? editingMetadata.keywords.split(',').map(k => k.trim()) : []
        }
      }, getAuthHeaders());

      toast.success('Metadata saved successfully');
      setMetadataModalOpen(false);
      fetchDocuments(selectedFolder.id);
    } catch (error) {
      toast.error('Failed to save metadata');
    } finally {
      setSavingMetadata(false);
    }
  };

  // Search functionality
  const handleSearch = async () => {
    if (!searchFilters.query && !searchFilters.document_type && !searchFilters.status) {
      toast.error('Please enter a search query or select filters');
      return;
    }

    setSearching(true);
    try {
      const response = await axios.post(`${API_URL}/api/documents/search`, {
        ...searchFilters,
        study_id: selectedStudy
      }, getAuthHeaders());
      setSearchResults(response.data);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  // Parse natural language query to extract search criteria
  const parseNaturalLanguageQuery = (query) => {
    const lowerQuery = query.toLowerCase();
    const results = [];
    
    // Parse date patterns
    const datePatterns = [
      /(?:uploaded|created|signed|submitted|approved|rejected)\s+(?:on\s+)?(\d{1,2})(?:st|nd|rd|th)?\s+(january|february|march|april|may|june|july|august|september|october|november|december)\s+(\d{4})/i,
      /(\d{1,2})(?:st|nd|rd|th)?\s+(january|february|march|april|may|june|july|august|september|october|november|december)\s+(\d{4})/i,
      /(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/
    ];
    
    // Parse status keywords
    const statusKeywords = {
      'waiting for approval': 'pending_review',
      'waiting for approvals': 'pending_review',
      'pending approval': 'pending_review',
      'pending review': 'pending_review',
      'needs approval': 'pending_review',
      'awaiting approval': 'pending_review',
      'in review': 'in_review',
      'being reviewed': 'in_review',
      'under review': 'in_review',
      'approved': 'approved',
      'rejected': 'rejected',
      'draft': 'draft',
      'archived': 'archived'
    };
    
    // Parse document type keywords
    const docTypeKeywords = {
      'protocol': 'Protocol',
      'protocols': 'Protocol',
      'cda': 'CDA',
      'confidential disclosure': 'CDA',
      'safety': 'Safety Report',
      'safety report': 'Safety Report',
      'safety document': 'Safety Report',
      'safety documents': 'Safety Report',
      'consent': 'Consent Form',
      'consent form': 'Consent Form',
      'icf': 'Consent Form',
      'informed consent': 'Consent Form',
      'regulatory': 'Regulatory',
      'sop': 'SOP',
      'training': 'Training',
      'report': 'Report',
      'correspondence': 'Correspondence',
      'amendment': 'Amendment'
    };
    
    // Parse version patterns
    const versionMatch = lowerQuery.match(/version\s*(\d+)/i);
    
    // Parse signer/author patterns
    const signerMatch = lowerQuery.match(/signed\s+by\s+([a-zA-Z\s]+?)(?:\s*,|\s*$|\s+on|\s+in)/i);
    const authorMatch = lowerQuery.match(/(?:uploaded|created|submitted)\s+by\s+([a-zA-Z\s]+?)(?:\s*,|\s*$|\s+on|\s+in)/i);
    
    // Build filter criteria
    let filterCriteria = {
      searchTerms: [],
      status: null,
      documentType: null,
      version: null,
      signer: null,
      author: null,
      dateFilter: null
    };
    
    // Check for status
    for (const [keyword, status] of Object.entries(statusKeywords)) {
      if (lowerQuery.includes(keyword)) {
        filterCriteria.status = status;
        break;
      }
    }
    
    // Check for document type
    for (const [keyword, docType] of Object.entries(docTypeKeywords)) {
      if (lowerQuery.includes(keyword)) {
        filterCriteria.documentType = docType;
        break;
      }
    }
    
    // Check for version
    if (versionMatch) {
      filterCriteria.version = parseInt(versionMatch[1]);
    }
    
    // Check for signer
    if (signerMatch) {
      filterCriteria.signer = signerMatch[1].trim();
    }
    
    // Check for author
    if (authorMatch) {
      filterCriteria.author = authorMatch[1].trim();
    }
    
    // Parse date
    for (const pattern of datePatterns) {
      const match = query.match(pattern);
      if (match) {
        try {
          const months = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
          let day, month, year;
          
          if (match[2] && isNaN(match[2])) {
            // Month name format
            day = parseInt(match[1]);
            month = months.indexOf(match[2].toLowerCase());
            year = parseInt(match[3]);
          } else {
            // Numeric format
            day = parseInt(match[1]);
            month = parseInt(match[2]) - 1;
            year = parseInt(match[3]);
          }
          
          filterCriteria.dateFilter = new Date(year, month, day).toISOString().split('T')[0];
        } catch (e) {
          console.log('Date parsing error', e);
        }
        break;
      }
    }
    
    return filterCriteria;
  };

  // Apply natural language search filter
  const applyNaturalLanguageSearch = (query) => {
    const criteria = parseNaturalLanguageQuery(query);
    
    let filtered = [...documents];
    let appliedFilters = [];
    
    // Filter by status
    if (criteria.status) {
      filtered = filtered.filter(doc => doc.status === criteria.status);
      appliedFilters.push(`Status: ${criteria.status.replace(/_/g, ' ')}`);
    }
    
    // Filter by document type
    if (criteria.documentType) {
      filtered = filtered.filter(doc => 
        doc.document_type?.toLowerCase().includes(criteria.documentType.toLowerCase()) ||
        doc.name?.toLowerCase().includes(criteria.documentType.toLowerCase())
      );
      appliedFilters.push(`Type: ${criteria.documentType}`);
    }
    
    // Filter by version
    if (criteria.version) {
      filtered = filtered.filter(doc => doc.current_version === criteria.version);
      appliedFilters.push(`Version: ${criteria.version}`);
    }
    
    // Filter by signer
    if (criteria.signer) {
      filtered = filtered.filter(doc => 
        doc.signatures?.some(sig => 
          sig.signer_name?.toLowerCase().includes(criteria.signer.toLowerCase()) ||
          sig.signer_email?.toLowerCase().includes(criteria.signer.toLowerCase())
        )
      );
      appliedFilters.push(`Signed by: ${criteria.signer}`);
    }
    
    // Filter by author/uploader
    if (criteria.author) {
      filtered = filtered.filter(doc => 
        doc.uploaded_by_name?.toLowerCase().includes(criteria.author.toLowerCase())
      );
      appliedFilters.push(`Uploaded by: ${criteria.author}`);
    }
    
    // Filter by date
    if (criteria.dateFilter) {
      filtered = filtered.filter(doc => {
        const docDate = doc.created_at?.split('T')[0];
        return docDate === criteria.dateFilter;
      });
      appliedFilters.push(`Date: ${criteria.dateFilter}`);
    }
    
    // If no specific filters matched, do text search
    if (appliedFilters.length === 0) {
      const searchTerms = query.toLowerCase().split(' ').filter(t => t.length > 2);
      filtered = filtered.filter(doc => {
        const searchText = `${doc.name} ${doc.document_type || ''} ${doc.description || ''} ${doc.uploaded_by_name || ''}`.toLowerCase();
        return searchTerms.some(term => searchText.includes(term));
      });
      appliedFilters.push(`Keywords: ${query}`);
    }
    
    return { filtered, appliedFilters };
  };

  // Voice search functionality
  const startVoiceSearch = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error('Voice search is not supported in your browser. Try Chrome or Edge.');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setIsListening(true);
      toast.info('🎤 Listening... Speak your search query', { duration: 3000 });
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setSearchQuery(transcript);
      
      // Apply natural language search
      const { filtered, appliedFilters } = applyNaturalLanguageSearch(transcript);
      
      if (filtered.length > 0) {
        setSmartSearchResults(filtered);
        toast.success(
          <div>
            <div className="font-medium">Found {filtered.length} document{filtered.length !== 1 ? 's' : ''}</div>
            <div className="text-xs mt-1 opacity-80">{appliedFilters.join(' • ')}</div>
          </div>,
          { duration: 4000 }
        );
      } else {
        setSmartSearchResults([]);
        toast.info(`No documents found for: "${transcript}"`, { duration: 3000 });
      }
    };

    recognition.onerror = (event) => {
      setIsListening(false);
      if (event.error === 'no-speech') {
        toast.error('No speech detected. Please try again.');
      } else if (event.error === 'not-allowed') {
        toast.error('Microphone access denied. Please allow microphone access.');
      } else {
        toast.error('Voice recognition error. Please try again.');
      }
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.start();
  };

  // Bulk operations
  const handleBulkSelect = (docId) => {
    setSelectedDocs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(docId)) {
        newSet.delete(docId);
      } else {
        newSet.add(docId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedDocs.size === documents.length) {
      setSelectedDocs(new Set());
    } else {
      setSelectedDocs(new Set(documents.map(d => d.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedDocs.size} documents?`)) return;

    try {
      const response = await axios.post(`${API_URL}/api/documents/bulk`, {
        document_ids: Array.from(selectedDocs),
        action: 'delete'
      }, getAuthHeaders());

      toast.success(`Deleted ${response.data.success} documents`);
      setSelectedDocs(new Set());
      setBulkMode(false);
      fetchDocuments(selectedFolder.id);
    } catch (error) {
      toast.error('Bulk delete failed');
    }
  };

  const handleBulkMove = async () => {
    if (!targetFolderId) {
      toast.error('Please select a target folder');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/documents/bulk`, {
        document_ids: Array.from(selectedDocs),
        action: 'move',
        target_folder_id: targetFolderId
      }, getAuthHeaders());

      toast.success(`Moved ${response.data.success} documents`);
      setSelectedDocs(new Set());
      setBulkMode(false);
      setShowBulkMoveModal(false);
      fetchDocuments(selectedFolder.id);
    } catch (error) {
      toast.error('Bulk move failed');
    }
  };

  const handleBulkArchive = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/documents/bulk`, {
        document_ids: Array.from(selectedDocs),
        action: 'archive'
      }, getAuthHeaders());

      toast.success(`Archived ${response.data.success} documents`);
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} documents could not be archived (must be approved)`);
      }
      setSelectedDocs(new Set());
      setBulkMode(false);
      fetchDocuments(selectedFolder.id);
    } catch (error) {
      toast.error('Bulk archive failed');
    }
  };

  // Export functionality
  const handleExportStudy = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/api/studies/${selectedStudy}/export`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'study_documents.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Study exported successfully');
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleExportSelected = async () => {
    if (selectedDocs.size === 0) {
      toast.error('No documents selected');
      return;
    }

    setExporting(true);
    try {
      const response = await axios.post(`${API_URL}/api/documents/export`, 
        Array.from(selectedDocs),
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'selected_documents.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Documents exported successfully');
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleCreateStudy = async () => {
    if (!newStudyName) {
      toast.error('Please enter a study name');
      return;
    }

    setCreatingStudy(true);
    try {
      await axios.post(`${API_URL}/api/studies`, {
        name: newStudyName,
        description: newStudyDescription,
        study_start_date: studyStartDate || null,
        first_patient_in: firstPatientIn || null,
        last_patient_in: lastPatientIn || null,
        last_patient_last_visit: lastPatientLastVisit || null,
        database_lock: databaseLock || null,
        study_completion: studyCompletion || null,
        phase: studyPhase || null,
        therapeutic_area: therapeuticArea || null
      }, getAuthHeaders());

      toast.success('Study created successfully with document schedule');
      setNewStudyModalOpen(false);
      // Reset all fields
      setNewStudyName('');
      setNewStudyDescription('');
      setStudyStartDate('');
      setFirstPatientIn('');
      setLastPatientIn('');
      setLastPatientLastVisit('');
      setDatabaseLock('');
      setStudyCompletion('');
      setStudyPhase('');
      setTherapeuticArea('');
      fetchStudies();
    } catch (error) {
      toast.error('Failed to create study');
    } finally {
      setCreatingStudy(false);
    }
  };

  // Fetch pending document requirements for current study
  const fetchPendingRequirements = async (studyId) => {
    try {
      const response = await axios.get(`${API_URL}/api/studies/${studyId}/pending-requirements`, getAuthHeaders());
      setPendingRequirements(response.data.pending_requirements || []);
    } catch (error) {
      console.log('Could not fetch pending requirements');
    }
  };

  // Fetch document schedule for current study
  const fetchDocumentSchedule = async (studyId) => {
    try {
      const response = await axios.get(`${API_URL}/api/studies/${studyId}/document-schedule`, getAuthHeaders());
      setDocumentSchedule(response.data);
    } catch (error) {
      console.log('Could not fetch document schedule');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getPreviewUrl = () => {
    // Handle both 'file_content' (from regular doc endpoint) and 'content' (from preview endpoint)
    const base64Content = previewContent?.file_content || previewContent?.content;
    if (!base64Content) return null;
    const mimeTypes = {
      pdf: 'application/pdf',
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      gif: 'image/gif',
      webp: 'image/webp',
      svg: 'image/svg+xml'
    };
    const mimeType = mimeTypes[previewContent.file_type?.toLowerCase()] || 'application/octet-stream';
    return `data:${mimeType};base64,${base64Content}`;
  };

  const isPreviewable = (fileType) => {
    const previewableTypes = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'doc', 'docx', 'txt', 'md', 'csv', 'json', 'xml', 'html', 'css', 'js', 'py', 'log'];
    return previewableTypes.includes(fileType?.toLowerCase());
  };

  const getFileTypeClass = (type) => {
    const t = type?.toLowerCase();
    if (t === 'pdf') return 'pdf';
    if (['doc', 'docx'].includes(t)) return 'doc';
    if (['xls', 'xlsx'].includes(t)) return 'xls';
    return 'default';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Use getDisplayDocuments instead of filteredDocuments
  const filteredDocuments = getDisplayDocuments();

  const getWorkflowActions = (status) => {
    const actions = {
      draft: [{ action: 'submit_for_review', label: 'Submit for Review', icon: Send }],
      pending_review: [
        { action: 'start_review', label: 'Start Review', icon: Eye },
        { action: 'approve', label: 'Approve', icon: CheckCircle },
        { action: 'reject', label: 'Reject', icon: XCircle }
      ],
      in_review: [
        { action: 'approve', label: 'Approve', icon: CheckCircle },
        { action: 'reject', label: 'Reject', icon: XCircle }
      ],
      rejected: [
        { action: 'revert_to_draft', label: 'Revert to Draft', icon: RefreshCw },
        { action: 'submit_for_review', label: 'Resubmit', icon: Send }
      ],
      approved: [
        { action: 'archive', label: 'Archive', icon: Archive },
        { action: 'supersede', label: 'Supersede', icon: RefreshCw }
      ],
      archived: [],
      superseded: []
    };
    return actions[status] || [];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center dashboard-bg">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen dashboard-bg flex flex-col ${isDark ? 'dark' : ''}`}>
      {/* Header - Extracted to DashboardHeader component */}
      <DashboardHeader
        user={user}
        studies={studies}
        selectedStudy={selectedStudy}
        setSelectedStudy={setSelectedStudy}
        contextStudy={contextStudy}
        clearStudySelection={clearStudySelection}
        isAdmin={isAdmin}
        canExport={canExport}
        exporting={exporting}
        isDark={isDark}
        toggleTheme={toggleTheme}
        handleLogout={handleLogout}
        handleExportStudy={handleExportStudy}
        setShowSearchModal={setShowSearchModal}
        setMfaSettingsOpen={setMfaSettingsOpen}
        setShowAllDocs={setShowAllDocs}
        setSelectedFolder={setSelectedFolder}
        setSearchQuery={setSearchQuery}
        setSmartSearchResults={setSmartSearchResults}
      />

      {/* Main Content - with padding-top to account for fixed header */}
      <div className="flex-1 flex pt-14 sm:pt-16">
        {/* Persistent Folder Sidebar - FR-01 to FR-08 compliant */}
        <PersistentFolderSidebar
          folders={folders}
          selectedFolder={selectedFolder}
          expandedFolders={expandedFolders}
          folderSubfolders={folderSubfolders}
          onFolderClick={handleFolderClick}
          onSubfolderClick={handleSubfolderClick}
          onExpandedChange={setExpandedFolders}
          onMobileClose={() => setMobileSidebarOpen(false)}
          isMobileOpen={mobileSidebarOpen}
        />

        {/* Main Content Area - Light/Dark Mode Support */}
        {/* Dynamic left margin to account for fixed sidebar on desktop */}
        <main 
          className="flex-1 flex flex-col overflow-y-auto bg-gradient-to-br from-slate-200/80 via-slate-100 to-blue-50/50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 relative transition-[margin] duration-300 ease-in-out"
          style={{ 
            marginLeft: isDesktop 
              ? (sidebarCollapsed ? COLLAPSED_WIDTH : sidebarWidth) 
              : 0 
          }}
          data-testid="main-content-area"
        >
          {/* Animated background effects */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-red-500/8 dark:bg-red-500/10 rounded-full blur-[120px] animate-pulse"></div>
            <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-indigo-500/6 dark:bg-indigo-500/8 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-cyan-500/5 dark:bg-cyan-500/8 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '2s' }}></div>
          </div>

          {/* Toolbar */}
          <div className="min-h-[52px] sm:min-h-[56px] px-3 sm:px-6 py-2 border-b border-slate-300 dark:border-slate-600/60 dark:border-slate-700/60 bg-slate-100 dark:bg-slate-800/60 dark:bg-slate-800/60 backdrop-blur-xl flex flex-wrap items-center justify-between gap-2 relative z-10">
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              {/* Mobile Folder Toggle Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileSidebarOpen(true)}
                className="lg:hidden h-8 w-8 p-0 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300"
                data-testid="mobile-folder-toggle"
              >
                <FolderTree className="w-4 h-4" />
              </Button>

              {/* All Documents Toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setShowAllDocs(true); setSelectedFolder(null); }}
                className={`h-8 px-3 sm:px-4 rounded-lg text-xs sm:text-sm ${showAllDocs ? 'bg-red-600 text-white' : 'bg-slate-100 dark:bg-slate-800/80 dark:bg-slate-700/80 border border-slate-300 dark:border-slate-600 dark:border-slate-600 text-slate-700 dark:text-slate-300 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600'}`}
              >
                All Docs
              </Button>

              {/* Current Folder Display (if folder selected) - Hidden on small mobile */}
              {selectedFolder && !showAllDocs && (
                <div className="hidden xs:flex items-center gap-2 px-2 sm:px-3 py-1.5 bg-slate-100 dark:bg-slate-800/80 rounded-lg border border-slate-300 dark:border-slate-600">
                  <Folder className="w-4 h-4 text-red-600" />
                  <span className="text-xs sm:text-sm text-slate-900 dark:text-slate-100 max-w-[100px] sm:max-w-[180px] truncate" title={selectedFolder.name}>
                    {selectedFolder.name}
                  </span>
                </div>
              )}

              {/* Sort Dropdown - Always visible but compact on mobile */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-24 sm:w-32 h-8 bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 text-xs sm:text-sm">
                  <SelectValue placeholder="Sort..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600">
                  <SelectItem value="latest" className="text-gray-700 hover:bg-slate-200">Latest First</SelectItem>
                  <SelectItem value="oldest" className="text-gray-700 hover:bg-slate-200">Oldest First</SelectItem>
                  <SelectItem value="name" className="text-gray-700 hover:bg-slate-200">Name (A-Z)</SelectItem>
                  <SelectItem value="status" className="text-gray-700 hover:bg-slate-200">By Status</SelectItem>
                  <SelectItem value="folder" className="text-gray-700 hover:bg-slate-200">By Folder</SelectItem>
                  <SelectItem value="compliance" className="text-gray-700 hover:bg-slate-200">By Compliance</SelectItem>
                </SelectContent>
              </Select>

              {/* Site Filter Dropdown - Hidden on mobile */}
              <Select 
                value={searchFilters.site} 
                onValueChange={(value) => setSearchFilters(prev => ({ ...prev, site: value === 'all' ? '' : value }))}
              >
                <SelectTrigger className="hidden md:flex w-36 h-8 bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 text-sm">
                  <Building className="w-3 h-3 mr-1 text-slate-500" />
                  <SelectValue placeholder="All Sites" />
                </SelectTrigger>
                <SelectContent className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600">
                  <SelectItem value="all" className="text-gray-700 hover:bg-slate-200">All Sites</SelectItem>
                  {studySites.map((site) => (
                    <SelectItem key={site.id} value={site.name} className="text-gray-700 hover:bg-slate-200">
                      <div className="flex items-center gap-1">
                        <span>{site.name}</span>
                        {site.code && <span className="text-xs text-slate-500">({site.code})</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Country Filter Dropdown - Hidden on mobile */}
              <Select 
                value={searchFilters.country} 
                onValueChange={(value) => setSearchFilters(prev => ({ ...prev, country: value === 'all' ? '' : value }))}
              >
                <SelectTrigger className="hidden md:flex w-32 h-8 bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 text-sm">
                  <MapPin className="w-3 h-3 mr-1 text-slate-500" />
                  <SelectValue placeholder="All Countries" />
                </SelectTrigger>
                <SelectContent className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600">
                  <SelectItem value="all" className="text-gray-700 hover:bg-slate-200">All Countries</SelectItem>
                  {studyCountries.map((country) => (
                    <SelectItem key={country} value={country} className="text-gray-700 hover:bg-slate-200">
                      {country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Document Count */}
              <span className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">
                {getDisplayDocuments().length} docs
              </span>

              {/* Clear Smart Search Filter */}
              {smartSearchResults !== null && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearSmartSearch}
                  className="h-7 bg-amber-500/20 border-amber-500/30 text-amber-600 hover:bg-amber-500/30 text-xs"
                >
                  <X className="w-3 h-3 mr-1" />
                  <span className="hidden sm:inline">Clear</span>
                </Button>
              )}
            </div>

            <div className="flex items-center gap-1 sm:gap-2">
              {/* Quick Search with Voice - Hidden on small mobile, shown in header instead */}
              <div className="hidden sm:flex relative items-center gap-1">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400" />
                  <Input
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setSmartSearchResults(null); }}
                    className="pl-9 pr-2 w-28 md:w-36 h-8 bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400 text-sm"
                    data-testid="search-documents-input"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={startVoiceSearch}
                  disabled={isListening}
                  className={`hidden md:flex h-8 w-8 p-0 rounded-full transition-all ${
                    isListening 
                      ? 'bg-red-500 text-slate-900 dark:text-slate-100 animate-pulse' 
                      : 'bg-slate-100 dark:bg-slate-800/80 border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-100 hover:bg-slate-200'
                  }`}
                  title="Voice search"
                  data-testid="voice-search-btn"
                >
                  {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </Button>
              </div>

              {/* Bulk Mode Toggle */}
              {isEditor() && getDisplayDocuments().length > 0 && (
                <Button
                  variant={bulkMode ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setBulkMode(!bulkMode);
                    if (bulkMode) setSelectedDocs(new Set());
                  }}
                  className={`h-8 w-8 sm:w-auto sm:px-2 p-0 rounded-full ${bulkMode ? 'bg-red-600 text-slate-900 dark:text-slate-100' : 'bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-700 dark:text-slate-300 hover:bg-slate-200'}`}
                  title={bulkMode ? 'Cancel selection' : 'Bulk select documents'}
                >
                  <CheckSquare className="w-4 h-4" />
                  <span className="ml-1 hidden lg:inline">{bulkMode ? 'Cancel' : 'Bulk'}</span>
                </Button>
              )}

              {/* Bulk Actions - Compact */}
              {bulkMode && selectedDocs.size > 0 && (
                <div className="flex items-center gap-1">
                  <span className="text-xs text-slate-600 dark:text-slate-400 mr-1">{selectedDocs.size}</span>
                  {canEditMetadata() && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowBulkMoveModal(true)}
                      className="h-8 w-8 p-0 bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-700 dark:text-slate-300 hover:bg-slate-200"
                      title="Move"
                    >
                      <FolderInput className="w-4 h-4" />
                    </Button>
                  )}
                  {canExport() && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleExportSelected}
                      disabled={exporting}
                      className="h-8 w-8 p-0 bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-700 dark:text-slate-300 hover:bg-slate-200"
                      title="Export"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  )}
                  {canEditMetadata() && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleBulkArchive}
                      className="h-8 w-8 p-0 bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-700 dark:text-slate-300 hover:bg-slate-200"
                      title="Archive"
                    >
                      <Archive className="w-4 h-4" />
                    </Button>
                  )}
                  {canDelete() && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleBulkDelete}
                      className="h-8 w-8 p-0 bg-red-600 hover:bg-red-700"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              )}

              {isEditor() && selectedFolder && !bulkMode && canUploadToFolder(selectedFolder) && (
                <Button
                  onClick={() => setUploadModalOpen(true)}
                  data-testid="upload-document-btn"
                  className="h-8 sm:h-9 px-2 sm:px-4 rounded-full bg-red-500 hover:bg-red-600 text-white text-xs sm:text-sm"
                >
                  <Upload className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Upload</span>
                </Button>
              )}
              {isEditor() && selectedFolder && !bulkMode && !canUploadToFolder(selectedFolder) && (
                <>
                  {/* Show Smart Upload on main folder level */}
                  <Button
                    onClick={() => setSmartUploadModalOpen(true)}
                    data-testid="smart-upload-folder-btn"
                    className="h-8 sm:h-9 px-2 sm:px-4 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white text-xs sm:text-sm"
                  >
                    <Wand2 className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Smart Upload</span>
                  </Button>
                </>
              )}
              
              {/* Smart Upload Button - visible on All Docs view or when no folder selected */}
              {isEditor() && (showAllDocs || !selectedFolder) && !bulkMode && (
                <Button
                  onClick={() => setSmartUploadModalOpen(true)}
                  data-testid="smart-upload-btn"
                  className="h-8 sm:h-9 px-2 sm:px-4 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white text-xs sm:text-sm"
                >
                  <Wand2 className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Smart Upload</span>
                </Button>
              )}

              {/* Separator - Hidden on mobile */}
              <div className="hidden sm:block h-6 w-px bg-slate-200 mx-1" />

              {/* View Type Selector - Always visible */}
              <ViewTypeSwitcher viewType={viewType} setViewType={setViewType} />
            </div>
          </div>

          {/* Pending Actions Panel - Refactored Component */}
          <PendingActionsPanel
            pendingActions={pendingActions}
            pendingRequirements={pendingRequirements}
            showPendingActions={showPendingActions}
            setShowPendingActions={setShowPendingActions}
            user={user}
            setSelectedDocForWorkflow={setSelectedDocForWorkflow}
            setSignatureModalOpen={setSignatureModalOpen}
            onUploadForRequirement={(req) => {
              // Pre-fill document type for upload
              setUploadMetadata(prev => ({ ...prev, document_type: req.document_type }));
              setSmartUploadModalOpen(true);
            }}
          />

          {/* Documents Grid */}
          <ScrollArea className="flex-1 p-3 sm:p-6 bg-transparent">
            {!selectedStudy ? (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <Folder className="w-10 h-10 text-red-600" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">Select a Study</h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400">Choose a study from the dropdown to view documents</p>
              </div>
            ) : filteredDocuments.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <FileText className="w-10 h-10 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                  {showAllDocs ? 'No Documents in Study' : 'No Documents in Folder'}
                </h3>
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                  {showAllDocs 
                    ? 'This study has no documents yet. Select a subfolder to upload documents.'
                    : 'This folder is empty'}
                </p>
                {!showAllDocs && isEditor() && selectedFolder && canUploadToFolder(selectedFolder) && (
                  <Button
                    onClick={() => setUploadModalOpen(true)}
                    className="rounded-full bg-red-600 hover:bg-red-700 text-slate-900 dark:text-slate-100"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload First Document
                  </Button>
                )}
                {!showAllDocs && isEditor() && selectedFolder && !canUploadToFolder(selectedFolder) && (
                  <p className="text-sm text-amber-600">
                    Documents can only be uploaded to subfolders. Please select a subfolder.
                  </p>
                )}
              </div>
            ) : (
              <>
                {/* Select All checkbox in bulk mode */}
                {bulkMode && (
                  <div className="flex items-center gap-2 mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                    <Checkbox
                      checked={selectedDocs.size === filteredDocuments.length}
                      onCheckedChange={handleSelectAll}
                    />
                    <span className="text-sm text-blue-300">Select All ({filteredDocuments.length})</span>
                  </div>
                )}

                {/* View Type Renderers */}
                
                {/* Icons View - Refactored Component */}
                {viewType === 'icons' && (
                  <IconsView
                    documents={filteredDocuments}
                    selectedDocs={selectedDocs}
                    bulkMode={bulkMode}
                    handleBulkSelect={handleBulkSelect}
                    handlePreview={handlePreview}
                    handleDownload={handleDownload}
                    canSignDocument={canSignDocument}
                    isPreviewable={isPreviewable}
                  />
                )}

                {/* List View - Refactored Component */}
                {viewType === 'list' && (
                  <ListView
                    documents={filteredDocuments}
                    selectedDocs={selectedDocs}
                    bulkMode={bulkMode}
                    handleBulkSelect={handleBulkSelect}
                    handleDownload={handleDownload}
                    handleEditMetadata={handleEditMetadata}
                    handlePreview={handlePreview}
                    isPreviewable={isPreviewable}
                    canSignDocument={canSignDocument}
                    setSelectedDocForWorkflow={setSelectedDocForWorkflow}
                    setSignatureModalOpen={setSignatureModalOpen}
                    isEditor={isEditor}
                    canEditDocument={canEditDocument}
                    canDownload={canDownload}
                    canSign={canSign}
                    formatFileSize={formatFileSize}
                  />
                )}

                {/* Details View */}
                {viewType === 'details' && (
                  <div className="bg-slate-50 dark:bg-slate-800/90 rounded-lg border border-slate-300 dark:border-slate-600 overflow-hidden backdrop-blur-sm">
                    <table className="w-full">
                      <thead className="bg-slate-100 dark:bg-slate-800/80 border-b border-slate-300 dark:border-slate-600">
                        <tr>
                          {bulkMode && <th className="w-10 p-3"></th>}
                          <th className="text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase p-3">Name</th>
                          <th className="text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase p-3 w-20">Type</th>
                          <th className="text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase p-3 w-24">Size</th>
                          <th className="text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase p-3 w-28">Status</th>
                          <th className="text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase p-3 w-24">Compliance</th>
                          <th className="text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase p-3 w-32">Uploaded By</th>
                          <th className="text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase p-3 w-32">Date</th>
                          <th className="w-10 p-3"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200/50">
                        {filteredDocuments.map((doc) => {
                          const StatusIcon = STATUS_CONFIG[doc.status]?.icon || Edit;
                          return (
                            <tr 
                              key={doc.id} 
                              className={`hover:bg-slate-100 dark:hover:bg-slate-700/50 cursor-pointer ${selectedDocs.has(doc.id) ? 'bg-blue-100 dark:bg-blue-900/30' : ''}`}
                              onClick={() => {
                                if (bulkMode) {
                                  handleBulkSelect(doc.id);
                                } else if (isPreviewable(doc.file_type)) {
                                  handlePreview(doc);
                                }
                              }}
                            >
                              {bulkMode && (
                                <td className="p-3" onClick={(e) => e.stopPropagation()}>
                                  <Checkbox checked={selectedDocs.has(doc.id)} onCheckedChange={() => handleBulkSelect(doc.id)} />
                                </td>
                              )}
                              <td className="p-3">
                                <div className="flex items-center gap-2">
                                  {doc.file_type?.toLowerCase() === 'pdf' ? (
                                    <File className="w-4 h-4 text-red-600" />
                                  ) : (
                                    <FileText className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                                  )}
                                  <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{doc.name}</span>
                                  {canSignDocument(doc) && (
                                    <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                                  )}
                                </div>
                              </td>
                              <td className="p-3 text-xs text-slate-600 dark:text-slate-400 uppercase">{doc.file_type}</td>
                              <td className="p-3 text-xs text-slate-600 dark:text-slate-400">{formatFileSize(doc.file_size)}</td>
                              <td className="p-3">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_CONFIG[doc.status]?.color || 'bg-slate-200'}`}>
                                  <StatusIcon className="w-3 h-3" />
                                  {STATUS_CONFIG[doc.status]?.label || doc.status}
                                </span>
                              </td>
                              <td className="p-3">
                                {doc.compliance_score !== undefined && doc.compliance_score !== null ? (
                                  <span 
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${
                                      doc.compliance_score >= 80 
                                        ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/50 dark:text-green-300 dark:border-green-700' 
                                        : doc.compliance_score >= 60 
                                          ? 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/50 dark:text-amber-300 dark:border-amber-700'
                                          : 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/50 dark:text-red-300 dark:border-red-700'
                                    }`}
                                    title={`Status: ${doc.compliance_status || 'assessed'}`}
                                  >
                                    <Shield className="w-3 h-3" />
                                    {doc.compliance_score.toFixed(0)}%
                                  </span>
                                ) : (
                                  <span className="text-[10px] text-slate-400">—</span>
                                )}
                              </td>
                              <td className="p-3 text-xs text-slate-600 dark:text-slate-400">{doc.uploaded_by_name}</td>
                              <td className="p-3 text-xs text-slate-600 dark:text-slate-400">{new Date(doc.created_at).toLocaleDateString()}</td>
                              <td className="p-3" onClick={(e) => e.stopPropagation()}>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-100">
                                      <MoreVertical className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600">
                                    {isPreviewable(doc.file_type) && (
                                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handlePreview(doc); }} className="text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">Preview</DropdownMenuItem>
                                    )}
                                    {canDownload() && (
                                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDownload(doc); }} className="text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">Download</DropdownMenuItem>
                                    )}
                                    {canEditDocument(doc) && <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEditMetadata(doc); }} className="text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">Edit</DropdownMenuItem>}
                                    {canViewAuditLogs() && (
                                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleViewAuditLog(doc); }} className="text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700">Audit Trail</DropdownMenuItem>
                                    )}
                                    {canSignDocument(doc) && canSign() && (
                                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setSelectedDocForWorkflow(doc); setSignatureModalOpen(true); }} className="text-amber-400 hover:bg-amber-500/20">Sign Document</DropdownMenuItem>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Tiles View (Default - Original Card Layout) */}
                {viewType === 'tiles' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {filteredDocuments.map((doc, index) => {
                      const StatusIcon = STATUS_CONFIG[doc.status]?.icon || Edit;
                      return (
                        <div
                          key={doc.id}
                          className={`document-card animate-fade-in ${selectedDocs.has(doc.id) ? 'ring-2 ring-blue-500' : ''}`}
                          style={{ opacity: 0, animationDelay: `${index * 0.05}s` }}
                          data-testid={`document-card-${doc.id}`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-start gap-2">
                              {bulkMode && (
                                <Checkbox
                                  checked={selectedDocs.has(doc.id)}
                                  onCheckedChange={() => handleBulkSelect(doc.id)}
                                  className="mt-1"
                                />
                              )}
                              <div 
                                className="w-12 h-12 rounded-lg bg-slate-100 dark:bg-slate-800/80 border border-slate-300 dark:border-slate-600 flex items-center justify-center cursor-pointer hover:bg-slate-200/50 transition-colors"
                                onClick={() => isPreviewable(doc.file_type) && handlePreview(doc)}
                              >
                                {isPreviewable(doc.file_type) ? (
                                  doc.file_type?.toLowerCase() === 'pdf' ? (
                                    <File className="w-6 h-6 text-red-600" />
                                  ) : (
                                    <FileImage className="w-6 h-6 text-red-600" />
                                  )
                                ) : (
                                  <FileText className="w-6 h-6 text-slate-600 dark:text-slate-400" />
                                )}
                              </div>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-100">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600">
                                {isPreviewable(doc.file_type) && (
                                  <DropdownMenuItem onClick={() => handlePreview(doc)} className="text-gray-700 hover:bg-slate-200">
                                    <Eye className="w-4 h-4 mr-2" />
                                    Preview
                                  </DropdownMenuItem>
                                )}
                                {canDownload() && (
                                  <DropdownMenuItem onClick={() => handleDownload(doc)} className="text-gray-700 hover:bg-slate-200">
                                    <Download className="w-4 h-4 mr-2" />
                                    Download
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator className="bg-slate-200" />
                                {canEditDocument(doc) && (
                                  <DropdownMenuItem onClick={() => handleEditMetadata(doc)} className="text-gray-700 hover:bg-slate-200">
                                    <Edit className="w-4 h-4 mr-2" />
                                    Edit Metadata
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => handleViewVersions(doc)} className="text-gray-700 hover:bg-slate-200">
                                  <History className="w-4 h-4 mr-2" />
                                  Version History
                                </DropdownMenuItem>
                                {canViewAuditLogs() && (
                                  <DropdownMenuItem onClick={() => handleViewAuditLog(doc)} className="text-gray-700 hover:bg-slate-200">
                                    <Clock className="w-4 h-4 mr-2" />
                                    Audit Trail
                                  </DropdownMenuItem>
                                )}
                                {canManageWorkflow() && (
                                  <>
                                    <DropdownMenuSeparator className="bg-slate-200" />
                                    <DropdownMenuItem onClick={() => {
                                      setSelectedDocForWorkflow(doc);
                                      setWorkflowModalOpen(true);
                                    }} className="text-gray-700 hover:bg-slate-200">
                                      <Send className="w-4 h-4 mr-2" />
                                      Workflow Actions
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {canSignDocument(doc) && canSign() && (
                                  <DropdownMenuItem 
                                    onClick={() => {
                                      setSelectedDocForWorkflow(doc);
                                      setSignatureModalOpen(true);
                                    }}
                                    className="bg-amber-500/20 text-amber-300 font-medium hover:bg-amber-500/30"
                                  >
                                    <PenLine className="w-4 h-4 mr-2 text-amber-600" />
                                    ✍️ Sign Document
                                    <span className="ml-auto text-xs bg-amber-500/30 px-1 rounded">Required</span>
                                  </DropdownMenuItem>
                                )}
                                {canDelete() && (
                                  <>
                                    <DropdownMenuSeparator className="bg-slate-200" />
                                    <DropdownMenuItem
                                      onClick={() => handleDeleteDocument(doc.id)}
                                      className="text-red-600 hover:bg-red-500/20"
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>

                          <h4 className="font-medium text-slate-900 dark:text-slate-100 mb-1 truncate" title={doc.name}>
                            {doc.name}
                          </h4>
                          
                          <div className="flex items-center gap-2 flex-wrap text-xs text-slate-600 dark:text-slate-400 mb-2">
                            <span className={`file-type-badge ${getFileTypeClass(doc.file_type)}`}>
                              {doc.file_type?.toUpperCase()}
                            </span>
                            <span>{formatFileSize(doc.file_size)}</span>
                            {doc.version > 1 && (
                              <span className="px-1.5 py-0.5 bg-red-500/20 text-blue-300 rounded text-[10px] font-medium">
                                v{doc.version}
                              </span>
                            )}
                          </div>

                          {/* Status Badge */}
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_CONFIG[doc.status]?.color || 'bg-slate-200 text-slate-700 dark:text-slate-300'}`}>
                              <StatusIcon className="w-3 h-3" />
                              {STATUS_CONFIG[doc.status]?.label || doc.status}
                            </span>
                            {doc.is_filed && (
                              <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 rounded text-[10px] font-semibold border border-emerald-200 dark:border-emerald-700">
                                Filed
                              </span>
                            )}
                            {/* Compliance Score Badge */}
                            {doc.compliance_score !== undefined && doc.compliance_score !== null && (
                              <span 
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${
                                  doc.compliance_score >= 80 
                                    ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/50 dark:text-green-300 dark:border-green-700' 
                                    : doc.compliance_score >= 60 
                                      ? 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/50 dark:text-amber-300 dark:border-amber-700'
                                      : 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/50 dark:text-red-300 dark:border-red-700'
                                }`}
                                title={`Compliance: ${doc.compliance_status || 'assessed'}`}
                                data-testid={`compliance-badge-${doc.id}`}
                              >
                                <Shield className="w-3 h-3" />
                                {doc.compliance_score.toFixed(0)}%
                              </span>
                            )}
                          </div>

                          {/* Signature Status */}
                          {doc.assignees_with_roles?.length > 0 && (
                            <div className="mb-2 p-2 bg-slate-100 dark:bg-slate-800/80 rounded border border-slate-300 dark:border-slate-600">
                              {(() => {
                                const status = getSignatureStatus(doc);
                                const hasSignatureRequirements = status.required > 0;
                                
                                return (
                                  <>
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-[10px] font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                                        <Shield className="w-3 h-3" /> 
                                        {hasSignatureRequirements ? 'Signatures' : 'Assigned'}
                                      </span>
                                      {hasSignatureRequirements ? (
                                        <span className={`text-[10px] font-medium ${
                                          status.signed >= status.required ? 'text-green-600' : 'text-amber-600'
                                        }`}>
                                          {status.signed}/{status.required}
                                        </span>
                                      ) : (
                                        <span className="text-[10px] text-slate-500 dark:text-slate-400">No sig required</span>
                                      )}
                                    </div>
                                    <div className="flex gap-1 flex-wrap">
                                      {doc.assignees_with_roles.map((assignee, idx) => {
                                        const hasSigned = doc.signatures?.some(
                                          sig => sig.signer_email?.toLowerCase() === assignee.email?.toLowerCase()
                                        );
                                        const sigRequired = assignee.signature_required === true;
                                        
                                        return (
                                          <span 
                                            key={idx} 
                                            className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium ${
                                              hasSigned 
                                                ? 'bg-green-500/20 text-green-600 border border-green-500/30' 
                                                : sigRequired 
                                                  ? 'bg-amber-500/20 text-amber-600 border border-amber-500/30' 
                                                  : 'bg-slate-200 text-slate-600 dark:text-slate-400'
                                            }`}
                                            title={`${assignee.email} - ${assignee.role}`}
                                          >
                                            {hasSigned ? (
                                              <CheckCircle className="w-2.5 h-2.5" />
                                            ) : sigRequired ? (
                                              <PenLine className="w-2.5 h-2.5" />
                                            ) : (
                                              <User className="w-2.5 h-2.5" />
                                            )}
                                            {assignee.role === 'reviewer' ? 'R' : assignee.role === 'approver' ? 'A' : 'QC'}
                                          </span>
                                        );
                                      })}
                                    </div>
                                    {canSignDocument(doc) && (
                                      <p className="text-[9px] text-amber-600 mt-1 animate-pulse">
                                        ⚡ Your signature required
                                      </p>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          )}

                          {/* Metadata Preview */}
                          {showAllDocs && (
                            <p className="text-xs text-red-600 bg-red-500/20 border border-red-500/30 px-2 py-0.5 rounded inline-block mb-1">
                              <Folder className="w-3 h-3 inline mr-1" />
                              {doc.folder_path || getFolderPath(doc.folder_id)}
                            </p>
                          )}
                          {doc.document_type && (
                            <p className="text-xs text-slate-600 dark:text-slate-400">
                              Type: {doc.document_type}
                            </p>
                          )}
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                            by {doc.uploaded_by_name}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Content View */}
                {viewType === 'content' && (
                  <div className="space-y-4">
                    {filteredDocuments.map((doc) => {
                      const StatusIcon = STATUS_CONFIG[doc.status]?.icon || Edit;
                      return (
                        <div
                          key={doc.id}
                          className={`bg-slate-50 dark:bg-slate-800/90 rounded-lg border border-slate-300 dark:border-slate-600 p-4 hover:border-slate-400 dark:border-slate-500 transition-all backdrop-blur-sm ${selectedDocs.has(doc.id) ? 'ring-2 ring-blue-500' : ''}`}
                        >
                          <div className="flex gap-4">
                            {/* Preview Area */}
                            <div 
                              className="w-32 h-32 rounded-lg bg-slate-100 dark:bg-slate-800/80 border border-slate-300 dark:border-slate-600 flex items-center justify-center cursor-pointer hover:bg-slate-200/50 flex-shrink-0"
                              onClick={() => isPreviewable(doc.file_type) && handlePreview(doc)}
                            >
                              {doc.file_type?.toLowerCase() === 'pdf' ? (
                                <File className="w-12 h-12 text-red-600" />
                              ) : ['jpg', 'jpeg', 'png', 'gif'].includes(doc.file_type?.toLowerCase()) ? (
                                <FileImage className="w-12 h-12 text-red-600" />
                              ) : (
                                <FileText className="w-12 h-12 text-slate-600 dark:text-slate-400" />
                              )}
                            </div>
                            
                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <h4 className="font-semibold text-slate-900 dark:text-slate-100">{doc.name}</h4>
                                  <p className="text-sm text-slate-600 dark:text-slate-400">
                                    {doc.file_type?.toUpperCase()} • {formatFileSize(doc.file_size)} • v{doc.version || 1}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  {canSignDocument(doc) && canSign() && (
                                    <Button 
                                      size="sm" 
                                      className="bg-amber-500 hover:bg-amber-600 rounded-full"
                                      onClick={() => { setSelectedDocForWorkflow(doc); setSignatureModalOpen(true); }}
                                    >
                                      <PenLine className="w-4 h-4 mr-1" /> Sign
                                    </Button>
                                  )}
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="outline" size="sm" className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 hover:bg-slate-200">
                                        Actions <ChevronDown className="w-4 h-4 ml-1" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600">
                                      {isPreviewable(doc.file_type) && (
                                        <DropdownMenuItem onClick={() => handlePreview(doc)} className="text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700">Preview</DropdownMenuItem>
                                      )}
                                      {canDownload() && (
                                        <DropdownMenuItem onClick={() => handleDownload(doc)} className="text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700">Download</DropdownMenuItem>
                                      )}
                                      {canEditDocument(doc) && <DropdownMenuItem onClick={() => handleEditMetadata(doc)} className="text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700">Edit Metadata</DropdownMenuItem>}
                                      <DropdownMenuItem onClick={() => handleViewVersions(doc)} className="text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700">Version History</DropdownMenuItem>
                                      {canViewAuditLogs() && (
                                        <DropdownMenuItem onClick={() => handleViewAuditLog(doc)} className="text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700">Audit Trail</DropdownMenuItem>
                                      )}
                                      {canManageWorkflow() && (
                                        <>
                                          <DropdownMenuSeparator className="bg-slate-200 dark:bg-slate-600" />
                                          <DropdownMenuItem onClick={() => { setSelectedDocForWorkflow(doc); setWorkflowModalOpen(true); }} className="text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700">Workflow</DropdownMenuItem>
                                        </>
                                      )}
                                      {canDelete() && (
                                        <DropdownMenuItem onClick={() => handleDeleteDocument(doc.id)} className="text-red-600 hover:bg-red-500/20">Delete</DropdownMenuItem>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-3 mb-3">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CONFIG[doc.status]?.color || 'bg-slate-200'}`}>
                                  <StatusIcon className="w-3 h-3" />
                                  {STATUS_CONFIG[doc.status]?.label || doc.status}
                                </span>
                                {doc.document_type && (
                                  <span className="text-xs text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 px-2 py-0.5 rounded">{doc.document_type}</span>
                                )}
                              </div>
                              
                              {doc.description && (
                                <p className="text-sm text-slate-600 dark:text-slate-400 mb-2 line-clamp-2">{doc.description}</p>
                              )}
                              
                              <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
                                <span>Uploaded by {doc.uploaded_by_name}</span>
                                <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                                {doc.assignees_with_roles?.length > 0 && (
                                  <span className="flex items-center gap-1">
                                    <Users className="w-3 h-3" />
                                    {doc.assignees_with_roles.length} assigned
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </ScrollArea>
        </main>
      </div>

      {/* Upload Modal with Metadata */}
      <UploadModal
        isOpen={uploadModalOpen}
        onClose={setUploadModalOpen}
        uploadName={uploadName}
        setUploadName={setUploadName}
        uploadFile={uploadFile}
        setUploadFile={setUploadFile}
        uploadMetadata={uploadMetadata}
        setUploadMetadata={setUploadMetadata}
        autoClassifyEnabled={autoClassifyEnabled}
        setAutoClassifyEnabled={setAutoClassifyEnabled}
        autoClassifying={autoClassifying}
        autoClassifyConfidence={autoClassifyConfidence}
        setAutoClassifyConfidence={setAutoClassifyConfidence}
        documentTypes={documentTypes}
        suggestedDocTypes={suggestedDocTypes}
        handleDocumentTypeChange={handleDocumentTypeChange}
        handleFileSelect={handleFileSelect}
        handleUpload={handleUpload}
        uploading={uploading}
        resetUploadForm={resetUploadForm}
      />

      {/* Smart Upload Modal - with folder selection and auto-classification */}
      <Dialog open={smartUploadModalOpen} onOpenChange={setSmartUploadModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-purple-600" />
              Smart Upload with Auto-Classification
            </DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400">
              Upload a document and let the system automatically detect document type, target folder, and metadata based on the filename.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* File Selection with Auto-Classify */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-slate-700 dark:text-slate-300">File *</Label>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  <span className="text-xs text-purple-600">Auto-classification enabled</span>
                </div>
              </div>
              <div className="border-2 border-dashed border-purple-500/50 rounded-lg p-6 text-center bg-slate-100 dark:bg-slate-800/80 hover:bg-slate-100 dark:bg-slate-800 transition-colors">
                {uploadFile ? (
                  <div className="flex items-center justify-center gap-2 flex-wrap">
                    <FileText className="w-5 h-5 text-purple-600" />
                    <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{uploadFile.name}</span>
                    {(autoClassifying || suggestingFolder) && <Loader2 className="w-4 h-4 animate-spin text-purple-600" />}
                    {autoClassifyConfidence > 0 && !autoClassifying && !suggestingFolder && (
                      <Badge variant="outline" className={`text-xs ${autoClassifyConfidence > 0.5 ? 'border-green-500 text-green-600' : 'border-yellow-500 text-yellow-400'}`}>
                        <Sparkles className="w-3 h-3 mr-1" />
                        {Math.round(autoClassifyConfidence * 100)}% classified
                      </Badge>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => { setUploadFile(null); setAutoClassifyConfidence(0); setSmartUploadTargetFolder(null); setFolderSuggestions([]); }} className="h-6 w-6 p-0 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-100">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <Wand2 className="w-8 h-8 mx-auto text-purple-600 mb-2" />
                    <p className="text-sm text-slate-600 dark:text-slate-400">Click to select a file</p>
                    <p className="text-xs text-purple-600 mt-1">Document type, target folder, site & country will be auto-detected</p>
                    <input type="file" className="hidden" onChange={(e) => handleFileSelect(e.target.files[0])} />
                  </label>
                )}
              </div>
            </div>

            {/* Auto-detected summary */}
            {uploadFile && autoClassifyConfidence > 0 && (
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                <div className="flex items-center gap-2 text-purple-600 text-sm font-medium mb-2">
                  <Sparkles className="w-4 h-4" />
                  Auto-Detected from Filename
                </div>
                <div className="flex flex-wrap gap-2">
                  {uploadMetadata.document_type && (
                    <Badge className="bg-red-500/20 text-blue-300 border-red-500/30">
                      Type: {uploadMetadata.document_type}
                    </Badge>
                  )}
                  {smartUploadTargetFolder && (
                    <Badge className="bg-green-500/20 text-green-300 border-green-500/30">
                      <Folder className="w-3 h-3 mr-1" />
                      {smartUploadTargetFolder.fullPath}
                    </Badge>
                  )}
                  {uploadMetadata.site && (
                    <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30">
                      Site: {uploadMetadata.site}
                    </Badge>
                  )}
                  {uploadMetadata.country && (
                    <Badge className="bg-teal-500/20 text-teal-300 border-teal-500/30">
                      Country: {uploadMetadata.country}
                    </Badge>
                  )}
                </div>
              </div>
            )}

            {/* Target Folder Selection - with auto-suggestion */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-slate-700 dark:text-slate-300">Target Folder *</Label>
                {suggestingFolder && (
                  <div className="flex items-center gap-1 text-xs text-purple-600">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Finding best folder...
                  </div>
                )}
                {smartUploadTargetFolder && !suggestingFolder && (
                  <Badge variant="outline" className="text-xs border-green-500 text-green-600">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    {folderSuggestions.length > 0 ? 'Auto-selected' : 'Selected'}
                  </Badge>
                )}
              </div>
              
              {/* Show folder suggestions with confidence percentages */}
              {folderSuggestions.length > 0 && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 text-sm font-medium">
                      <FolderOpen className="w-4 h-4" />
                      {smartUploadTargetFolder ? 'Suggested folders (click to change):' : 'Select target folder:'}
                    </div>
                    {folderSuggestions.length > 5 && (
                      <span className="text-xs text-slate-500">Showing top 5 of {folderSuggestions.length}</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-400 mb-2">
                    Folders are ranked by match confidence. Select Main Folder → Subfolder → Document Type
                  </p>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {folderSuggestions.slice(0, 5).map((suggestion) => {
                      // Parse the full_path to show hierarchy clearly
                      const pathParts = suggestion.full_path?.split(' → ') || [suggestion.folder_name];
                      const subfolderName = pathParts[pathParts.length - 1];
                      const parentPath = pathParts.slice(0, -1).join(' → ') || suggestion.parent_name || 'Root';
                      
                      return (
                        <button
                          key={suggestion.folder_id}
                          onClick={() => {
                            const folderObj = {
                              id: suggestion.folder_id,
                              name: suggestion.folder_name,
                              parentName: suggestion.parent_name,
                              fullPath: suggestion.full_path
                            };
                            setSmartUploadTargetFolder(folderObj);
                            // Use artifact_classification from suggestion if available
                            const artifactClass = suggestion.artifact_classification || 
                              generateArtifactClassification(uploadMetadata.document_type, folderObj);
                            setUploadMetadata(prev => ({ ...prev, artifact_classification: artifactClass }));
                            toast.success(`Selected: ${suggestion.full_path}`);
                          }}
                          className={`w-full flex flex-col p-3 rounded-lg text-left transition-colors border ${
                            smartUploadTargetFolder?.id === suggestion.folder_id 
                              ? 'bg-green-500/20 border-green-500/50' 
                              : 'bg-slate-100 dark:bg-slate-800/80 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 border-slate-300 dark:border-slate-600/80'
                          }`}
                        >
                          <div className="flex items-center justify-between w-full">
                            <div className="flex items-center gap-2">
                              <FolderOpen className="w-4 h-4 text-red-600 flex-shrink-0" />
                              <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{subfolderName}</span>
                              {smartUploadTargetFolder?.id === suggestion.folder_id && (
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                              )}
                            </div>
                            <Badge variant="outline" className={`text-xs flex-shrink-0 ${
                              (suggestion.confidence || 0) >= 0.9 
                                ? 'border-green-500 text-green-600 bg-green-500/10' 
                                : (suggestion.confidence || 0) >= 0.7 
                                  ? 'border-amber-500 text-amber-600 bg-amber-500/10' 
                                  : 'border-slate-400 dark:border-slate-500 text-slate-600 dark:text-slate-400'
                            }`}>
                              {Math.round((suggestion.confidence || 0) * 100)}% match
                            </Badge>
                          </div>
                          <div className="flex items-center gap-1 mt-1 ml-6 text-xs text-slate-600 dark:text-slate-400">
                            <span>Path:</span>
                            <span className="text-slate-700 dark:text-slate-300">{parentPath} → {subfolderName}</span>
                          </div>
                          {/* Show artifact classification preview */}
                          {suggestion.artifact_classification && (
                            <div className="mt-1 ml-6 text-xs text-blue-600 dark:text-blue-400">
                              Artifact: {suggestion.artifact_classification}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Regular folder dropdown - only show if no suggestions */}
              {folderSuggestions.length === 0 && (
                <Select 
                  value={smartUploadTargetFolder?.id || ''} 
                  onValueChange={(v) => {
                    const folder = getUploadableFolders().find(f => f.id === v);
                    setSmartUploadTargetFolder(folder);
                    setShowFolderConfirmation(false);
                    // Generate artifact classification with the selected folder
                    if (uploadMetadata.document_type && folder) {
                      const artifactClass = generateArtifactClassification(uploadMetadata.document_type, folder);
                      setUploadMetadata(prev => ({ ...prev, artifact_classification: artifactClass }));
                    }
                  }}
                >
                  <SelectTrigger className={`bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 ${smartUploadTargetFolder ? 'border-green-500/50' : ''}`}>
                    <SelectValue placeholder="Auto-detected or select manually" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600 max-h-60">
                    {getUploadableFolders().map(folder => (
                      <SelectItem key={folder.id} value={folder.id} className="text-gray-700 hover:bg-slate-200">
                        <div className="flex items-center gap-2">
                          <Folder className="w-4 h-4 text-red-600" />
                          <span>{folder.fullPath}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Document Name and Type */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300">Document Name *</Label>
                <Input 
                  placeholder="Enter document name" 
                  value={uploadName} 
                  onChange={(e) => setUploadName(e.target.value)}
                  className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300">Document Type *</Label>
                <Select value={uploadMetadata.document_type} onValueChange={handleDocumentTypeChange}>
                  <SelectTrigger className={`bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 ${uploadMetadata.document_type ? 'border-green-500/50' : ''}`}>
                    <SelectValue placeholder="Auto-detected or select" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600 max-h-60">
                    {suggestedDocTypes.length > 0 && (
                      <div className="px-2 py-1.5 text-xs font-semibold text-purple-600 dark:text-purple-400 border-b border-slate-200 dark:border-slate-700">
                        <Sparkles className="w-3 h-3 inline mr-1" />
                        Matching types based on filename
                      </div>
                    )}
                    {getSortedDocumentTypes().map((type, index) => {
                      const isSuggested = suggestedDocTypes.includes(type);
                      const isFirstNonSuggested = suggestedDocTypes.length > 0 && !isSuggested && 
                        (index === 0 || suggestedDocTypes.includes(getSortedDocumentTypes()[index - 1]));
                      return (
                        <React.Fragment key={type}>
                          {isFirstNonSuggested && (
                            <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 border-t border-slate-200 dark:border-slate-700 mt-1">
                              All document types
                            </div>
                          )}
                          <SelectItem 
                            value={type} 
                            className={`${isSuggested 
                              ? 'text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/20 font-medium' 
                              : 'text-gray-700 dark:text-slate-300'} hover:bg-slate-200 dark:hover:bg-slate-700`}
                          >
                            <span className="flex items-center gap-2">
                              {isSuggested && <Sparkles className="w-3 h-3 text-purple-500" />}
                              {type}
                            </span>
                          </SelectItem>
                        </React.Fragment>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300">Document Date *</Label>
                <Input type="date" value={uploadMetadata.document_date} onChange={(e) => setUploadMetadata(prev => ({ ...prev, document_date: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300">Effective Date</Label>
                <Input type="date" value={uploadMetadata.effective_date} onChange={(e) => setUploadMetadata(prev => ({ ...prev, effective_date: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300">Filing Date</Label>
                <Input type="date" value={uploadMetadata.filing_date} onChange={(e) => setUploadMetadata(prev => ({ ...prev, filing_date: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300">Receipt Date</Label>
                <Input type="date" value={uploadMetadata.receipt_date} onChange={(e) => setUploadMetadata(prev => ({ ...prev, receipt_date: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100" />
              </div>
            </div>

            {/* Site and Country Dropdowns */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300">Site(s)</Label>
                <Select 
                  value={uploadMetadata.site} 
                  onValueChange={(value) => {
                    if (value === 'all') {
                      setUploadMetadata(prev => ({ ...prev, site: 'All Sites', sites: [] }));
                    } else if (value === 'multiple') {
                      // Keep current selection but switch to multiple mode
                      setUploadMetadata(prev => ({ ...prev, site: '', sites: prev.sites }));
                    } else {
                      setUploadMetadata(prev => ({ ...prev, site: value, sites: [] }));
                    }
                  }}
                >
                  <SelectTrigger className={`bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 ${uploadMetadata.site ? 'border-green-500/50' : ''}`}>
                    <SelectValue placeholder="Select site(s)">
                      {uploadMetadata.site || (uploadMetadata.sites?.length > 0 ? `${uploadMetadata.sites.length} sites selected` : 'Select site(s)')}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 max-h-60">
                    <SelectItem value="all" className="text-slate-900 dark:text-slate-100">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-blue-500" />
                        All Sites
                      </div>
                    </SelectItem>
                    <SelectItem value="multiple" className="text-slate-900 dark:text-slate-100">
                      <div className="flex items-center gap-2">
                        <CheckSquare className="w-4 h-4 text-purple-500" />
                        Select Multiple...
                      </div>
                    </SelectItem>
                    {studySites.length > 0 ? (
                      studySites.map((site) => (
                        <SelectItem key={site.id} value={site.name} className="text-slate-900 dark:text-slate-100">
                          <div className="flex items-center gap-2">
                            <Building className="w-4 h-4 text-slate-500" />
                            <span>{site.name}</span>
                            {site.code && <span className="text-xs text-slate-500">({site.code})</span>}
                            {site.country && <span className="text-xs text-blue-500 ml-1">{site.country}</span>}
                          </div>
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-sites" disabled className="text-slate-500">
                        No sites configured - contact admin
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {/* Multi-select checkboxes when "Select Multiple" is chosen */}
                {!uploadMetadata.site && uploadMetadata.sites !== undefined && studySites.length > 0 && (
                  <div className="mt-2 p-2 bg-slate-50 dark:bg-slate-800/50 rounded border border-slate-200 dark:border-slate-700 max-h-32 overflow-y-auto">
                    <p className="text-xs text-slate-500 mb-2">Select applicable sites:</p>
                    {studySites.map((site) => (
                      <label key={site.id} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 px-1 rounded">
                        <input
                          type="checkbox"
                          checked={uploadMetadata.sites?.includes(site.name) || false}
                          onChange={(e) => {
                            const newSites = e.target.checked
                              ? [...(uploadMetadata.sites || []), site.name]
                              : (uploadMetadata.sites || []).filter(s => s !== site.name);
                            setUploadMetadata(prev => ({ ...prev, sites: newSites }));
                          }}
                          className="rounded border-slate-300"
                        />
                        <span className="text-sm text-slate-700 dark:text-slate-300">{site.name}</span>
                        {site.code && <span className="text-xs text-slate-500">({site.code})</span>}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300">Country/Countries</Label>
                <Select 
                  value={uploadMetadata.country} 
                  onValueChange={(value) => {
                    if (value === 'all') {
                      setUploadMetadata(prev => ({ ...prev, country: 'All Countries', countries: [] }));
                    } else if (value === 'multiple') {
                      setUploadMetadata(prev => ({ ...prev, country: '', countries: prev.countries }));
                    } else {
                      setUploadMetadata(prev => ({ ...prev, country: value, countries: [] }));
                    }
                  }}
                >
                  <SelectTrigger className={`bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 ${uploadMetadata.country ? 'border-green-500/50' : ''}`}>
                    <SelectValue placeholder="Select country/countries">
                      {uploadMetadata.country || (uploadMetadata.countries?.length > 0 ? `${uploadMetadata.countries.length} countries selected` : 'Select country/countries')}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 max-h-60">
                    <SelectItem value="all" className="text-slate-900 dark:text-slate-100">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-blue-500" />
                        All Countries
                      </div>
                    </SelectItem>
                    <SelectItem value="multiple" className="text-slate-900 dark:text-slate-100">
                      <div className="flex items-center gap-2">
                        <CheckSquare className="w-4 h-4 text-purple-500" />
                        Select Multiple...
                      </div>
                    </SelectItem>
                    {studyCountries.length > 0 ? (
                      studyCountries.map((country) => (
                        <SelectItem key={country} value={country} className="text-slate-900 dark:text-slate-100">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-slate-500" />
                            {country}
                          </div>
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-countries" disabled className="text-slate-500">
                        No countries configured - contact admin
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {/* Multi-select checkboxes when "Select Multiple" is chosen */}
                {!uploadMetadata.country && uploadMetadata.countries !== undefined && studyCountries.length > 0 && (
                  <div className="mt-2 p-2 bg-slate-50 dark:bg-slate-800/50 rounded border border-slate-200 dark:border-slate-700 max-h-32 overflow-y-auto">
                    <p className="text-xs text-slate-500 mb-2">Select applicable countries:</p>
                    {studyCountries.map((country) => (
                      <label key={country} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 px-1 rounded">
                        <input
                          type="checkbox"
                          checked={uploadMetadata.countries?.includes(country) || false}
                          onChange={(e) => {
                            const newCountries = e.target.checked
                              ? [...(uploadMetadata.countries || []), country]
                              : (uploadMetadata.countries || []).filter(c => c !== country);
                            setUploadMetadata(prev => ({ ...prev, countries: newCountries }));
                          }}
                          className="rounded border-slate-300"
                        />
                        <span className="text-sm text-slate-700 dark:text-slate-300">{country}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Owner and Contact */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300">Owner</Label>
                <Input placeholder="Document owner" value={uploadMetadata.owner} onChange={(e) => setUploadMetadata(prev => ({ ...prev, owner: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300">Contact</Label>
                <Input placeholder="Contact person" value={uploadMetadata.contact} onChange={(e) => setUploadMetadata(prev => ({ ...prev, contact: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400" />
              </div>
            </div>

            {/* Artifact Classification */}
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Artifact Classification (Binder/Section)</Label>
              <Input 
                placeholder="Auto-generated from folder path and document type" 
                value={uploadMetadata.artifact_classification} 
                onChange={(e) => setUploadMetadata(prev => ({ ...prev, artifact_classification: e.target.value }))}
                className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400">Auto-populated based on folder path and document type. You can edit if needed.</p>
            </div>

            {/* Keywords */}
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Keywords (comma-separated)</Label>
              <Input 
                placeholder="Auto-detected or enter manually" 
                value={uploadMetadata.keywords} 
                onChange={(e) => setUploadMetadata(prev => ({ ...prev, keywords: e.target.value }))} 
                className={`bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400 ${uploadMetadata.keywords ? 'border-green-500/50' : ''}`} 
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Description</Label>
              <Input placeholder="Brief description of the document" value={uploadMetadata.description} onChange={(e) => setUploadMetadata(prev => ({ ...prev, description: e.target.value }))} className="bg-slate-100 dark:bg-slate-800 border-slate-400 dark:border-slate-500 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSmartUploadModalOpen(false); resetUploadForm(); }} className="rounded-full bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:text-slate-900 dark:text-slate-100">
              Cancel
            </Button>
            <Button 
              onClick={handleSmartUpload} 
              disabled={uploading || !smartUploadTargetFolder || !uploadFile || !uploadMetadata.document_type || !uploadMetadata.document_date} 
              className="rounded-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-slate-900 dark:text-slate-100"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <Wand2 className="w-4 h-4 mr-2" />
                  Upload & Auto-File
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Metadata Edit Modal */}
      <MetadataEditModal
        isOpen={metadataModalOpen}
        onClose={setMetadataModalOpen}
        editingMetadata={editingMetadata}
        setEditingMetadata={setEditingMetadata}
        documentTypes={documentTypes}
        handleSaveMetadata={handleSaveMetadata}
        savingMetadata={savingMetadata}
      />

      {/* Workflow Modal */}
      <WorkflowModal
        isOpen={workflowModalOpen}
        onClose={setWorkflowModalOpen}
        selectedDocForWorkflow={selectedDocForWorkflow}
        workflowAssignees={workflowAssignees}
        setWorkflowAssignees={setWorkflowAssignees}
        workflowComment={workflowComment}
        setWorkflowComment={setWorkflowComment}
        processingWorkflow={processingWorkflow}
        handleWorkflowAction={handleWorkflowAction}
        addAssignee={addAssignee}
        removeAssignee={removeAssignee}
        updateAssignee={updateAssignee}
        getWorkflowActions={getWorkflowActions}
        getSignatureStatus={getSignatureStatus}
      />

      {/* Electronic Signature Modal - 21 CFR Part 11 Compliant */}
      <SignatureModal
        isOpen={signatureModalOpen}
        onClose={setSignatureModalOpen}
        selectedDocForWorkflow={selectedDocForWorkflow}
        user={user}
        signatureText={signatureText}
        setSignatureText={setSignatureText}
        signatureMeaning={signatureMeaning}
        setSignatureMeaning={setSignatureMeaning}
        signaturePassword={signaturePassword}
        setSignaturePassword={setSignaturePassword}
        signatureRole={signatureRole}
        setSignatureRole={setSignatureRole}
        processingSignature={processingSignature}
        handleSignDocument={handleSignDocument}
      />

      {/* Smart Search Panel */}
      <SmartSearchPanel
        open={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        onApplyFilters={(filtered, filterState) => {
          // Set the filtered results
          setSmartSearchResults(filtered);
          // Close modal and show success message
          setShowSearchModal(false);
          toast.success(`Found ${filtered.length} documents matching your filters`);
        }}
        documents={showAllDocs ? allStudyDocuments : documents}
        currentUser={user}
        documentTypes={documentTypes}
        availableSites={studySites}
        availableCountries={studyCountries}
      />

      {/* Bulk Move Modal */}
      <BulkMoveModal
        isOpen={showBulkMoveModal}
        onClose={setShowBulkMoveModal}
        selectedDocsCount={selectedDocs.size}
        folders={folders}
        targetFolderId={targetFolderId}
        setTargetFolderId={setTargetFolderId}
        handleBulkMove={handleBulkMove}
      />

      {/* New Study Modal */}
      <NewStudyModal
        isOpen={newStudyModalOpen}
        onClose={setNewStudyModalOpen}
        newStudyName={newStudyName}
        setNewStudyName={setNewStudyName}
        newStudyDescription={newStudyDescription}
        setNewStudyDescription={setNewStudyDescription}
        studyPhase={studyPhase}
        setStudyPhase={setStudyPhase}
        therapeuticArea={therapeuticArea}
        setTherapeuticArea={setTherapeuticArea}
        studyStartDate={studyStartDate}
        setStudyStartDate={setStudyStartDate}
        firstPatientIn={firstPatientIn}
        setFirstPatientIn={setFirstPatientIn}
        lastPatientIn={lastPatientIn}
        setLastPatientIn={setLastPatientIn}
        lastPatientLastVisit={lastPatientLastVisit}
        setLastPatientLastVisit={setLastPatientLastVisit}
        databaseLock={databaseLock}
        setDatabaseLock={setDatabaseLock}
        studyCompletion={studyCompletion}
        setStudyCompletion={setStudyCompletion}
        handleCreateStudy={handleCreateStudy}
        creatingStudy={creatingStudy}
      />

      {/* Document Preview Modal */}
      <DocumentPreviewModal
        isOpen={previewModalOpen}
        onClose={setPreviewModalOpen}
        previewDocument={previewDocument}
        previewContent={previewContent}
        previewLoading={previewLoading}
        handleDownload={handleDownload}
        getPreviewUrl={getPreviewUrl}
      />

      {/* Version History Modal - Regulatory Compliant (21 CFR Part 11) */}
      <VersionHistoryModal
        isOpen={versionModalOpen}
        onClose={setVersionModalOpen}
        selectedDocForVersion={selectedDocForVersion}
        documentVersions={documentVersions}
        uploadingVersion={uploadingVersion}
        handleUploadNewVersion={handleUploadNewVersion}
        handleDownload={handleDownload}
        handleDownloadVersion={handleDownloadVersion}
        isEditor={isEditor}
        canUpload={isEditor}
        formatFileSize={formatFileSize}
      />

      {/* Audit Trail Modal - 21 CFR Part 11 Compliant */}
      <AuditTrailModalInline
        isOpen={auditModalOpen}
        onClose={setAuditModalOpen}
        selectedDocForVersion={selectedDocForVersion}
        auditLogs={auditLogs}
        getAuthHeaders={getAuthHeaders}
      />

      {/* MFA Settings Modal */}
      <MFASettings
        isOpen={mfaSettingsOpen}
        onClose={() => setMfaSettingsOpen(false)}
        getAuthHeaders={getAuthHeaders}
      />
    </div>
  );
}
